/**
 * MailboxPage — functional read view over connected external mailboxes.
 *
 * Account selector + server-folder list + message list + reading pane, reading
 * from the Vault-backed routes, with an inline "Sync now". This is the Phase-3
 * basic read experience; the rich 3-panel features (smart inbox, snooze,
 * compose, etc.) are re-layered in Phase 6. HTML bodies render via
 * `EmailHtmlBody` (sanitized + sandboxed iframe); text-only bodies fall back to
 * the plain-text pane.
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Virtuoso } from 'react-virtuoso';
import { api } from '@/lib/api';
import type {
  EmailMessageDetailDto,
  EmailMailboxAttachmentDto,
  EmailMessageDto,
  EmailFolderDto,
  EmailCategoryGroupDto,
  EmailBulkFilterScope,
} from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  RefreshCw,
  Inbox,
  Mail,
  MailOpen,
  Star,
  Paperclip,
  MailX,
  Ban,
  CalendarPlus,
  Plane,
  Download,
  Settings,
  Trash2,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  PenSquare,
  Reply,
  ReplyAll,
  Forward,
  Archive,
  Clock,
  AlarmClock,
  Users,
  UserCog,
  Filter,
  Sparkles,
  Tag,
  X,
  Eye,
  FolderInput,
  FolderPlus,
  UserPlus,
  UserCheck,
  Menu,
  ArrowLeft,
  Undo2,
  Redo2,
  CheckSquare,
  PanelTop,
  ChevronDown,
  ChevronRight,
  Search,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { format, isThisYear, isToday } from 'date-fns';
import { useEmailConnections } from '../hooks/useConnectAccount';
import {
  useEmailFolders,
  useEmailFoldersByAccount,
  useEmailMessages,
  useUnifiedEmailMessages,
  useEmailSearch,
  useEmailBySender,
  useEmailByCategory,
  useEmailMessage,
  useEmailCategories,
  useEmailLabels,
  useMessageAttachments,
  useSyncAccount,
  useSyncAccountStream,
  useBackfillAllStream,
  useSyncFolder,
  useBackfillFolder,
  useEmptyTrash,
  useMarkRead,
  useMoveMessage,
  useCopyMessage,
  useMoveMessageToAccount,
  useBulkMarkRead,
  useBulkStar,
  useBulkMove,
  useBulkDelete,
  useBulkMoveByFilter,
  useBulkFlagByFilter,
  useDiscardDraft,
  useScreenerMessages,
  useUnifiedScreenerMessages,
  useDecideSenderByAddress,
  useCreateFolder,
  useRenameFolder,
  useDeleteFolder,
  useUnsubscribeMessage,
  useAddCalendarInvite,
  formatSyncProgress,
  friendlySyncError,
} from '../hooks/useMailbox';
import { MailboxComposeDialog, type ComposeInitialDraft } from '../components/MailboxComposeDialog';
import { ScheduledSendsDialog } from '../components/ScheduledSendsDialog';
import { MailboxSnoozeDialog } from '../components/MailboxSnoozeDialog';
import { MailboxSnoozedDialog } from '../components/MailboxSnoozedDialog';
import { ComposerSettingsDialog } from '../components/ComposerSettingsDialog';
import { AddressBookDialog } from '../components/AddressBookDialog';
import { FiltersDialog } from '../components/FiltersDialog';
import { ScreenerDialog } from '../components/ScreenerDialog';
import { SpamSettingsDialog } from '../components/SpamSettingsDialog';
import { VacationResponderDialog } from '../components/VacationResponderDialog';
import {
  AttachmentPreviewDialog,
  isPreviewableAttachment,
  type PreviewAttachment,
} from '../components/AttachmentPreviewDialog';
import { SaveToVaultFolderDialog } from '../components/SaveToVaultFolderDialog';
import { SenderGroupsView } from '../components/SenderGroupsView';
import { CategoryGroupsView } from '../components/CategoryGroupsView';
import { CategoryManagerDialog } from '../components/CategoryManagerDialog';
import { LabelManagerDialog } from '../components/LabelManagerDialog';
import { LabelChips, LabelPickerPopover } from '../components/MessageLabels';
import { BulkCategorizeDialog } from '../components/BulkCategorizeDialog';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { FolderSidebarItem } from '../components/FolderSidebarItem';
import { ColumnResizer } from '../components/ColumnResizer';
import { FolderNameDialog } from '../components/FolderNameDialog';
import { sortFolders, buildFolderTree } from '../lib/folder-tree';
import { unsubscribeMethod } from '../lib/list-unsubscribe';
import { EmailHtmlBody } from '../components/EmailHtmlBody';
import { useSummarizeMessage, useCategorizeMessage } from '../hooks/useEmailAI';
import { useReportSpam, useNotSpam } from '../hooks/useSpam';
import { useContactByEmail } from '../hooks/useAddressBook';
import { AddToContactDialog } from '../components/AddToContactDialog';
import { useIsMobile, useSwipePrefs, type SwipeAction } from '../hooks/useMobile';
import { useEmailListPrefs, type PreviewLines } from '../hooks/useEmailListPrefs';
import { useEmailUserSettings } from '../hooks/useEmailUserSettings';
import {
  useEmailColumnWidths,
  useCollapsedEmailAccounts,
  useCollapsedEmailFolders,
  useEmailSearchMode,
  useEmailToolsCollapsed,
  EMAIL_COLUMN_BOUNDS,
} from '../hooks/useEmailLayoutPrefs';
import { useEmailActiveAccount } from '../hooks/useEmailActiveAccount';
import { useMailboxHistory } from '../hooks/useMailboxHistory';
import { useChrome } from '../../../context/ChromeContext';
import type { EmailSearchField } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Mailbox-list timestamp — always an actual date (never a relative time): the
 * date + time for today ("Jun 4, 3:42 PM"), month/day earlier this year
 * ("Jun 4"), and the year too for older mail ("Jun 4, 2022").
 */
function formatListDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  if (isToday(d)) return format(d, 'MMM d, h:mm a');
  return isThisYear(d) ? format(d, 'MMM d') : format(d, 'MMM d, yyyy');
}

/** Compact human size for a message (body + attachments). Empty for 0/unknown. */
function formatBytes(n?: number): string {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Normalize a Content-ID for `cid:` matching (strip angle brackets + case). */
function normalizeCid(cid: string): string {
  return cid.trim().replace(/^<|>$/g, '').toLowerCase();
}

/**
 * Rewrite a resumed draft's inline images: each `<img src="cid:<id>">` is pointed
 * at a fresh preview URL and re-tagged with `data-doc-id` so the editor renders it
 * and a re-save re-attaches it as a CID part. Inline images whose byte URL didn't
 * resolve are left as-is (they'll simply not preview). Exported for unit testing.
 */
export function rehydrateInlineImages(
  html: string,
  images: { documentId: string; contentId: string; url: string }[],
): string {
  if (images.length === 0 || typeof DOMParser === 'undefined') return html;
  const byCid = new Map(images.map((i) => [normalizeCid(i.contentId), i]));
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img').forEach((img) => {
    const src = img.getAttribute('src') ?? '';
    if (!src.toLowerCase().startsWith('cid:')) return;
    const hit = byCid.get(normalizeCid(src.slice(4)));
    if (!hit) return;
    img.setAttribute('src', hit.url);
    img.setAttribute('data-doc-id', hit.documentId);
  });
  return doc.body.innerHTML;
}

/**
 * Strip remote (`http(s)`) images from an original message's HTML before it is
 * quoted into the composer, so replying to a marketing email doesn't fire its
 * tracking pixels in the (non-sandboxed) editor. `cid:`/`data:` images can't
 * resolve in the composer either, so they're dropped too — a reply quote keeps
 * text/structure, not the original's images. Exported for unit testing.
 */
export function stripQuotedRemoteImages(html: string): string {
  if (!html.includes('<img') || typeof DOMParser === 'undefined') return html;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('img').forEach((img) => img.remove());
  return doc.body.innerHTML;
}

/** A row's minimal shape for capturing undo state (id + folder + flags). */
interface MsgLite {
  messageId: string;
  folderId: string;
  isRead: boolean;
  isStarred: boolean;
}

/** Sentinel account-selector value for the unified (all-accounts) inbox. */
const UNIFIED_ACCOUNT = '__all__';

/** System folders sort first in a fixed order; custom folders follow, A→Z. */
// `sortFolders` / `buildFolderTree` / `FOLDER_ROLE_ORDER` live in ../lib/folder-tree
// (pure, unit-tested there); imported at the top of this file.

type MoveRole = 'inbox' | 'archive' | 'spam' | 'trash';
// "Move to" destinations in the unified ("All accounts") view. A folder id
// belongs to a single account, so a cross-account selection can't target one
// folder id — it targets a system ROLE and the server resolves that to each
// message's own account folder (the `targetSystemRole` path). Encoded as
// `role:<role>` option values to distinguish them from folder ids.
const SYSTEM_MOVE_ROLES: { role: MoveRole; label: string }[] = [
  { role: 'inbox', label: 'Inbox' },
  { role: 'archive', label: 'Archive' },
  { role: 'spam', label: 'Spam' },
  { role: 'trash', label: 'Trash' },
];

/**
 * The whole current view (single-account folder or unified inbox) loads at once,
 * up to this cap. The list IS virtualized (react-virtuoso renders only on-screen
 * rows), so DOM size isn't the bottleneck — this bounds the FETCH: the client
 * pages the list internally in ≤1000-row chunks (see `fetchMessagesPaged`) and
 * stops early when a page comes back short, so a small folder is one request and
 * a large one is ceil(rows / 1000). 5000 covers the vast majority of folders;
 * beyond it, search finds older mail and whole-folder bulk actions go through the
 * by-filter path.
 */
const MESSAGE_LIST_MAX = 5000;

/** Cadence of the background auto-sync that keeps an open mailbox fresh. */
const AUTO_SYNC_INTERVAL_MS = 2 * 60_000;

// Tailwind clamp utility per preview-lines preference. Literal class names so
// the JIT compiler keeps them.
const PREVIEW_LINE_CLAMP: Record<PreviewLines, string> = {
  1: 'truncate',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

// Stable references for the virtualized list — inline object/function props
// would be new every render and make Virtuoso re-process unnecessarily.
// `scrollbarGutter: stable` reserves a lane for the vertical scrollbar so it
// never overlaps the row content (dates/size/star sit flush to the edge).
const VIRTUOSO_STYLE = { height: '100%', scrollbarGutter: 'stable' } as const;
const VIRTUOSO_VIEWPORT = { top: 400, bottom: 600 } as const;
export interface EmailThreadGroup {
  threadId: string;
  subject: string;
  /** Newest message in the thread (the flat list is date-descending). */
  latest: EmailMessageDto;
  messages: EmailMessageDto[];
  count: number;
  anyUnread: boolean;
  anyStarred: boolean;
}

/** A row in the (optionally thread-collapsed) message list. */
export type EmailDisplayRow =
  | { kind: 'message'; message: EmailMessageDto; indented?: boolean }
  | { kind: 'thread'; group: EmailThreadGroup };

/**
 * Collapse a flat, date-descending message list into conversation display rows.
 * Groups by `threadId` (threads ordered by their newest message's position); a
 * single-message thread stays a plain row, a multi-message thread becomes a
 * collapsible header followed — only when expanded — by its messages (newest
 * first). Exported for unit testing.
 */
export function buildThreadRows(
  messages: EmailMessageDto[],
  expanded: ReadonlySet<string>,
): EmailDisplayRow[] {
  const order: string[] = [];
  const groups = new Map<string, EmailMessageDto[]>();
  for (const m of messages) {
    const g = groups.get(m.threadId);
    if (g) g.push(m);
    else {
      groups.set(m.threadId, [m]);
      order.push(m.threadId);
    }
  }
  const rows: EmailDisplayRow[] = [];
  for (const threadId of order) {
    const msgs = groups.get(threadId)!;
    if (msgs.length === 1) {
      rows.push({ kind: 'message', message: msgs[0] });
      continue;
    }
    rows.push({
      kind: 'thread',
      group: {
        threadId,
        subject: msgs[0].subject,
        latest: msgs[0],
        messages: msgs,
        count: msgs.length,
        anyUnread: msgs.some((m) => !m.isRead),
        anyStarred: msgs.some((m) => m.isStarred),
      },
    });
    if (expanded.has(threadId)) {
      for (const m of msgs) rows.push({ kind: 'message', message: m, indented: true });
    }
  }
  return rows;
}

const computeDisplayRowKey = (_index: number, row: EmailDisplayRow) =>
  row.kind === 'thread' ? `t:${row.group.threadId}` : `m:${row.message.messageId}`;

/** A small pill toggle used for the quick-filter and search-scope rows. */
function FilterChip({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon?: LucideIcon;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent'
      }`}
    >
      {Icon && <Icon className="h-3 w-3" />} {label}
    </button>
  );
}

/**
 * Field toggles shown under the box in global-search mode. Combinable
 * (Apple-Mail-style) — selecting Sender + Subject searches both. None selected =
 * search every field.
 */
const SEARCH_FIELDS: { value: EmailSearchField; label: string }[] = [
  { value: 'from', label: 'Sender' },
  { value: 'recipient', label: 'Recipient' },
  { value: 'subject', label: 'Subject' },
  { value: 'body', label: 'Body' },
];

export function MailboxPage() {
  const { data: accounts, isLoading: accountsLoading } = useEmailConnections();
  // Persisted per-device so the selected inbox survives navigating to the
  // Accounts view and back, and closing/reopening the arche later.
  const [activeAccountId, setActiveAccountId] = useEmailActiveAccount();
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  // In the "All accounts" view you can focus a single folder of one account
  // WITHOUT leaving the merged view — the sidebar stays; only the message list
  // narrows. null = the merged all-inboxes list.
  const [unifiedFocus, setUnifiedFocus] = useState<{ accountId: string; folderId: string } | null>(
    null,
  );
  // The dedicated Screener inbox (held mail awaiting a decision) is its own view,
  // parallel to the folder list. Mutually exclusive with folder/unifiedFocus
  // navigation — the nav handlers below clear it.
  const [screenerView, setScreenerView] = useState(false);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  // The message currently being dragged (desktop drag-to-folder). Held in state
  // — not just dataTransfer — because the browser hides dataTransfer values
  // during dragover, and we need the source account/folder there to decide which
  // folders can accept the drop.
  const [draggingMessage, setDraggingMessage] = useState<{
    messageId: string;
    accountId: string;
    folderId: string;
  } | null>(null);
  // Two-step confirm guard for the destructive "empty trash" action.
  const [confirmEmptyTrash, setConfirmEmptyTrash] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeDraft, setComposeDraft] = useState<ComposeInitialDraft | undefined>(undefined);
  // Lite ref of the draft currently open in the composer, so "Discard draft"
  // routes through the same actTrash (→ Trash folder) path the row uses.
  const [composeDraftLite, setComposeDraftLite] = useState<MsgLite | null>(null);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozedListOpen, setSnoozedListOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [screenerOpen, setScreenerOpen] = useState(false);
  const [spamOpen, setSpamOpen] = useState(false);
  const [vacationOpen, setVacationOpen] = useState(false);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Drill-down from the "By Sender" overview: a lower-cased from-address the
  // list is narrowed to. This filters the ALREADY-LOADED folder view in memory
  // (exact from-address match) rather than re-querying the search endpoint —
  // instant, uncapped (the whole synced window, not the search route's 200
  // cap), and exact. null = no sender narrowing.
  const [senderFilter, setSenderFilter] = useState<string | null>(null);
  // Drill-down from the "By Category" overview: the category id the list is
  // narrowed to (the literal 'uncategorized' = threads with no category).
  // Category lives on the thread, so — unlike senderFilter — this narrows via a
  // server-side re-query (baked into the folder fetch). undefined = no narrowing.
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  // Drill-down to one label (message-level M2M). Narrows the list server-side
  // (a label is a direct message attribute). undefined = no narrowing. Labels
  // are account-scoped, so this pairs with the current account context.
  const [labelFilter, setLabelFilter] = useState<string | undefined>(undefined);
  // Which message fields a search query matches against — a combinable set
  // (empty = all fields). Applies to the global-search mode.
  const [searchFields, setSearchFields] = useState<Set<EmailSearchField>>(new Set());
  // How the top box behaves: 'filter' narrows the CURRENT view in memory
  // (instant); 'search' runs a server-side lookup across ALL accounts + folders
  // (Apple-style). Persisted per-device.
  const [searchMode, setSearchMode] = useEmailSearchMode();
  // Quick filters for the folder view (independent toggles, can combine).
  const [filterUnread, setFilterUnread] = useState(false);
  const [filterStarred, setFilterStarred] = useState(false);
  const [filterAttachments, setFilterAttachments] = useState(false);
  // Grouping overview: 'none' (the flat list), 'sender' ("By Sender"), or
  // 'category' ("By Category"). The three are mutually exclusive. The exclude
  // toggles below (skip starred/unread/attachments) are shared — they narrow
  // whichever overview is active, and both its counts and its bulk-action sets.
  const [groupMode, setGroupMode] = useState<'none' | 'sender' | 'category'>('none');
  const bySender = groupMode === 'sender';
  const byCategory = groupMode === 'category';
  // A grouping overview (either mode) is showing its cards in the main area.
  const anyGrouping = groupMode !== 'none';
  const [bySenderExcludeStarred, setBySenderExcludeStarred] = useState(false);
  const [bySenderExcludeUnread, setBySenderExcludeUnread] = useState(false);
  const [bySenderExcludeAttachments, setBySenderExcludeAttachments] = useState(false);
  // Category-management dialog (create/rename/recolor/delete) — opened from the
  // By Category overview's "Manage" button.
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  // Label-management dialog (create/rename/recolor/delete) — opened from the
  // reading-pane label picker. Account-scoped to the open message's account.
  const [labelManagerOpen, setLabelManagerOpen] = useState(false);
  // Bulk-categorize confirm dialog — opened from the Uncategorized card.
  const [bulkCategorizeOpen, setBulkCategorizeOpen] = useState(false);
  // Conversation-view (thread collapse) — which thread rows are expanded.
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  const [toolsCollapsed, toggleToolsCollapsed] = useEmailToolsCollapsed();
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Multi-select for bulk actions (move/archive/delete/read/star over a set).
  // Checkboxes are hidden until selection mode is toggled on.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Anchor row for Shift-click range selection — the last row toggled without
  // Shift. A Shift-click extends the selection from here to the clicked row.
  const [selectionAnchorId, setSelectionAnchorId] = useState<string | null>(null);
  // "Select all N in folder": when on, bulk actions hit the ENTIRE folder
  // server-side (by filter), not just the loaded rows. Any per-row change drops
  // back to a normal loaded-set selection.
  const [selectAllInFolder, setSelectAllInFolder] = useState(false);
  // Pending whole-folder destructive action awaiting confirmation.
  const [confirmWholeFolder, setConfirmWholeFolder] = useState<
    | { kind: 'archive' }
    | { kind: 'trash' }
    | { kind: 'move'; targetFolderId: string; folderName: string }
    | null
  >(null);
  // ── Folder management (sidebar ⋯ menu + New folder) ──
  // Create/rename share one name dialog; empty/delete each get a confirm.
  const [folderNameDialog, setFolderNameDialog] = useState<
    | { mode: 'create'; parentFolderId?: string; parentName?: string }
    | { mode: 'rename'; folderId: string; current: string }
    | null
  >(null);
  const [confirmEmptyFolder, setConfirmEmptyFolder] = useState<{
    folderId: string;
    name: string;
    role: string | null;
  } | null>(null);
  const [confirmDeleteFolder, setConfirmDeleteFolder] = useState<{
    folderId: string;
    name: string;
  } | null>(null);
  // Confirm guard for the bulk "delete selected" action.
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  // Undo/redo stack for reversible message actions.
  const history = useMailboxHistory();
  // Attachment preview + "save to Vault folder" targets (null = closed).
  const [previewAtt, setPreviewAtt] = useState<PreviewAttachment | null>(null);
  const [saveToVaultDoc, setSaveToVaultDoc] = useState<{
    attachmentId: string;
    fileName: string;
  } | null>(null);
  // Mobile: single-column layout (list ↔ reading view-switch) + a folder drawer
  // + swipe-to-act on message rows.
  const isMobile = useIsMobile();
  const [swipePrefs] = useSwipePrefs();
  const [listPrefs] = useEmailListPrefs();
  // Holder-wide (cross-account) feature opt-ins — Scheduled send / Snooze.
  const userSettings = useEmailUserSettings();
  // Resizable column widths (accounts + message-list) and the collapsed set of
  // accounts in the unified sidebar — both per-device UI prefs.
  const [columnWidths, setColumnWidths] = useEmailColumnWidths();
  const collapsedAccounts = useCollapsedEmailAccounts();
  const collapsedFolders = useCollapsedEmailFolders();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipedRef = useRef(false);

  // Focus mode (chrome hidden): MailboxPage owns the restore controls — placed
  // in the sidebar below so they don't shift the message list down.
  const { isHeadersHidden, showHeaders } = useChrome();
  const headersHidden = isHeadersHidden('arche_email');

  // A persisted selection can outlive what it named (an account removed on
  // another device, or a drop back to a single account that retires the unified
  // option), so reconcile it against the live list rather than trusting it
  // blindly — otherwise a stale value shows a dead, empty mailbox.
  const isUnified = activeAccountId === UNIFIED_ACCOUNT && (accounts?.length ?? 0) > 1;
  const savedAccountId =
    activeAccountId &&
    activeAccountId !== UNIFIED_ACCOUNT &&
    accounts?.some((a) => a.accountId === activeAccountId)
      ? activeAccountId
      : null;
  const accountId = isUnified
    ? undefined
    : (savedAccountId ?? accounts?.find((a) => a.isPrimary)?.accountId ?? accounts?.[0]?.accountId);
  const activeConn = accounts?.find((a) => a.accountId === accountId);
  const screenerEnabled = activeConn?.modules?.screenerEnabled === true;
  // Unified inbox applies the screener overlay if ANY connected account enables
  // it, but scoped to just those accounts (below) — the screener is a per-account
  // opt-in, so it must never hide an unscreened account's mail from All accounts.
  const screenerAccountIds = useMemo(
    () =>
      (accounts ?? []).filter((a) => a.modules?.screenerEnabled === true).map((a) => a.accountId),
    [accounts],
  );
  const anyScreenerEnabled = screenerAccountIds.length > 0;
  // The default account the sidebar "Tools" dialogs open on. Single-account mode
  // uses the active account; unified mode has no single active account, so seed
  // with the primary — each per-account dialog (Filters / Screener / Spam /
  // Settings) carries its own in-popup account picker to switch.
  const primaryAccountId =
    accounts?.find((a) => a.isPrimary)?.accountId ?? accounts?.[0]?.accountId;
  const toolsAccountId = isUnified ? primaryAccountId : accountId;
  // Scheduled/Snooze are now holder-wide (global), not per-account.
  const toolsScheduleEnabled = userSettings.features.scheduleEnabled;
  const toolsSnoozeEnabled = userSettings.features.snoozeEnabled;
  // accountId → address, for the per-message account badge in unified mode.
  const accountAddressById = useMemo(
    () => new Map((accounts ?? []).map((a) => [a.accountId, a.emailAddress])),
    [accounts],
  );

  // In the unified view the sidebar lists every account's folders (grouped by
  // account) so a message can be dragged into any of them. Fetch folders for all
  // accounts only while unified — `[]` otherwise keeps this a no-op single query
  // set. Keyed like `useEmailFolders`, so the two views share the cache.
  const allAccountIds = useMemo(() => (accounts ?? []).map((a) => a.accountId), [accounts]);
  const { foldersByAccount } = useEmailFoldersByAccount(isUnified ? allAccountIds : []);

  const { data: folders } = useEmailFolders(accountId);
  const inboxFolderId =
    activeFolderId ??
    folders?.find((f) => f.systemRole === 'inbox')?.folderId ??
    folders?.[0]?.folderId;
  // Enforce the screener only on the inbox view.
  const viewingInbox = folders?.find((f) => f.folderId === inboxFolderId)?.systemRole === 'inbox';
  // Effective content scope. `isMerged` = the combined all-inboxes list. Otherwise
  // the list is a single (account, folder): the focused folder in "All accounts"
  // mode, or the open folder in single-account mode.
  const isMerged = isUnified && !unifiedFocus;
  const contentAccountId = isUnified ? unifiedFocus?.accountId : accountId;
  const contentFolderId = isUnified ? unifiedFocus?.folderId : inboxFolderId;
  const focusedFolder =
    isUnified && unifiedFocus
      ? (foldersByAccount.get(unifiedFocus.accountId) ?? []).find(
          (f) => f.folderId === unifiedFocus.folderId,
        )
      : undefined;
  // Quick-filter params shared by the single-account and unified inbox queries.
  const quickFilters = {
    isRead: filterUnread ? false : undefined,
    isStarred: filterStarred ? true : undefined,
    hasAttachments: filterAttachments ? true : undefined,
  };
  const { data: singleMessages, isLoading: singleLoading } = useEmailMessages(contentAccountId, {
    folderId: contentFolderId,
    // Screener is a single-account inbox concern; a focused peek shows all mail.
    screener: !isUnified && screenerEnabled && viewingInbox,
    // By-Category drill-down narrows the folder list server-side (category is a
    // thread attribute, so it can't be filtered in memory like a sender).
    categoryId: categoryFilter,
    // Label drill-down (message-level) also narrows server-side.
    labelId: labelFilter,
    limit: MESSAGE_LIST_MAX,
    ...quickFilters,
  });
  const { data: unifiedMessages, isLoading: unifiedLoading } = useUnifiedEmailMessages(
    {
      folderSystemRole: 'inbox',
      screener: anyScreenerEnabled,
      screenerAccountIds,
      categoryId: categoryFilter,
      labelId: labelFilter,
      limit: MESSAGE_LIST_MAX,
      ...quickFilters,
    },
    isMerged,
  );
  // Screener inbox (held/pending-sender mail). One bounded fetch drives BOTH the
  // sidebar count badge (always) and the view (when active). Single-account uses
  // the account's inbox folder; unified aggregates across every account.
  const { data: screenerSingle, isLoading: screenerLoadingSingle } = useScreenerMessages(
    accountId,
    inboxFolderId,
    !isUnified && screenerEnabled,
  );
  const { data: screenerUnified, isLoading: screenerLoadingUnified } = useUnifiedScreenerMessages(
    isUnified && anyScreenerEnabled,
  );
  const screenerMessages = isUnified ? screenerUnified : screenerSingle;
  const screenerCount = screenerMessages?.length ?? 0;
  const decideSender = useDecideSenderByAddress();

  const folderMessages = screenerView
    ? screenerMessages
    : isMerged
      ? unifiedMessages
      : singleMessages;
  const folderLoading = screenerView
    ? isUnified
      ? screenerLoadingUnified
      : screenerLoadingSingle
    : isMerged
      ? unifiedLoading
      : singleLoading;
  // The top box narrows the list in one of two modes (see `searchMode`):
  //  • filter — an instant, in-memory narrowing of the CURRENT view.
  //  • search — a server-side lookup across ALL accounts + folders (Apple-style).
  const hasQuery = searchTerm.trim().length >= 2;
  const filterActive = hasQuery && searchMode === 'filter';
  const searchActive = hasQuery && searchMode === 'search';
  const searchFieldsArr = useMemo(() => [...searchFields], [searchFields]);
  const toggleSearchField = (f: EmailSearchField) =>
    setSearchFields((prev) => {
      const next = new Set(prev);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return next;
    });
  const { data: searchResults, isLoading: searchLoading } = useEmailSearch(
    // Only the server-search mode runs the query; filter mode stays client-side.
    searchActive ? searchTerm : '',
    // Global search spans every account (undefined) and every folder (undefined),
    // regardless of what's currently open.
    undefined,
    searchFieldsArr,
    undefined,
    quickFilters,
  );
  // "By Sender" overview — same scope as the inbox list; only fetched in that
  // mode. Its own skip-starred/skip-unread toggles narrow the groups + actions.
  const { data: senderGroups, isLoading: senderGroupsLoading } = useEmailBySender(
    contentAccountId,
    isMerged
      ? {
          folderSystemRole: 'inbox',
          screener: anyScreenerEnabled,
          excludeStarred: bySenderExcludeStarred,
          excludeUnread: bySenderExcludeUnread,
          excludeAttachments: bySenderExcludeAttachments,
        }
      : {
          folderId: contentFolderId,
          screener: !isUnified && screenerEnabled && viewingInbox,
          excludeStarred: bySenderExcludeStarred,
          excludeUnread: bySenderExcludeUnread,
          excludeAttachments: bySenderExcludeAttachments,
        },
    bySender,
  );
  // "By Category" overview — same scope + exclude toggles as By Sender; only
  // fetched in that mode. Groups the folder view by the thread's smart category.
  const { data: categoryGroups, isLoading: categoryGroupsLoading } = useEmailByCategory(
    contentAccountId,
    isMerged
      ? {
          folderSystemRole: 'inbox',
          screener: anyScreenerEnabled,
          excludeStarred: bySenderExcludeStarred,
          excludeUnread: bySenderExcludeUnread,
          excludeAttachments: bySenderExcludeAttachments,
        }
      : {
          folderId: contentFolderId,
          screener: !isUnified && screenerEnabled && viewingInbox,
          excludeStarred: bySenderExcludeStarred,
          excludeUnread: bySenderExcludeUnread,
          excludeAttachments: bySenderExcludeAttachments,
        },
    byCategory,
  );
  // Categories (for the By Category chip label + drill-down pill). Fetched only
  // once the overview or a drill-down is active; cached 5 min in the hook.
  const { data: emailCategories } = useEmailCategories(byCategory || categoryFilter !== undefined);
  const categoryFilterName =
    categoryFilter === undefined
      ? null
      : categoryFilter === 'uncategorized'
        ? 'Uncategorized'
        : (emailCategories?.find((c) => c.categoryId === categoryFilter)?.name ?? 'Category');
  // Labels for the current account — resolves the drill-down pill's name (and is
  // reused by the manager dialog). Account-scoped, fetched only when a label
  // filter is active. In the unified view a label filter still targets one
  // account (the label id belongs to it), so the focused account resolves it.
  const { data: accountLabels } = useEmailLabels(
    contentAccountId,
    !!contentAccountId && labelFilter !== undefined,
  );
  const labelFilterName =
    labelFilter === undefined
      ? null
      : (accountLabels?.find((l) => l.labelId === labelFilter)?.name ?? 'Label');
  // A sender drill-down narrows the already-loaded folder view in memory (exact,
  // uncapped) — no search round-trip. Sender-filter, filter-mode, and
  // search-mode are mutually exclusive (each entry point clears the others).
  const senderFilteredMessages = useMemo(() => {
    if (!senderFilter) return undefined;
    return (folderMessages ?? []).filter(
      (m) => (m.fromAddress ?? '').toLowerCase() === senderFilter,
    );
  }, [senderFilter, folderMessages]);
  // Filter mode: narrow the current view in memory by matching the query against
  // sender, recipient(s), subject, and snippet — instant, no server call.
  const queryFilteredMessages = useMemo(() => {
    if (!filterActive) return undefined;
    const q = searchTerm.trim().toLowerCase();
    return (folderMessages ?? []).filter((m) => {
      const haystack = [
        m.fromName,
        m.fromAddress,
        m.subject,
        m.snippet,
        ...(m.toAddresses ?? []).flatMap((a) => [a.name, a.email]),
      ];
      return haystack.some((v) => v && v.toLowerCase().includes(q));
    });
  }, [filterActive, searchTerm, folderMessages]);
  const messages = senderFilter
    ? senderFilteredMessages
    : filterActive
      ? queryFilteredMessages
      : searchActive
        ? searchResults
        : folderMessages;
  const messagesLoading =
    senderFilter || filterActive ? folderLoading : searchActive ? searchLoading : folderLoading;

  // Conversation view: collapse the flat list by thread when the global setting
  // is on; otherwise every message is its own row. The list stays a single
  // Virtuoso feed — only the row data changes.
  const threadingOn = userSettings.conversationThreadingEnabled;
  const displayRows = useMemo<EmailDisplayRow[]>(
    () =>
      threadingOn
        ? buildThreadRows(messages ?? [], expandedThreads)
        : (messages ?? []).map((m) => ({ kind: 'message', message: m })),
    [threadingOn, messages, expandedThreads],
  );
  // The folder the list is showing — the focused folder in "All accounts" mode,
  // else the open single-account folder.
  const activeFolder = isUnified
    ? focusedFolder
    : folders?.find((f) => f.folderId === inboxFolderId);
  // In outbound folders the sender is always the user — list the recipient
  // ("To") instead, matching every mainstream mail client.
  const activeFolderRole = activeFolder?.systemRole;
  // Filter mode still views the current folder (just narrowed), so recipient
  // display + trash affordances stay; only a global SEARCH spans folders.
  const showRecipients =
    !searchActive && (activeFolderRole === 'sent' || activeFolderRole === 'drafts');
  const viewingTrash = !isMerged && !searchActive && activeFolderRole === 'trash';
  const { data: openMessage } = useEmailMessage(activeMessageId ?? undefined);
  const { data: openAttachments } = useMessageAttachments(activeMessageId ?? undefined);
  const sync = useSyncAccount();
  const syncStream = useSyncAccountStream();
  const syncFolder = useSyncFolder();
  // Last failure from the JSON auto-sync / folder-sync path. Unlike the streamed
  // "Sync all", these mutations were fire-and-forget, so a failed background sync
  // silently left the list stale ("looks synced but isn't"). Surface the reason.
  const autoSyncError = syncFolder.error ?? sync.error;
  const backfill = useBackfillFolder();
  const emptyTrash = useEmptyTrash();
  const markRead = useMarkRead();
  const moveMessage = useMoveMessage();
  const copyMessage = useCopyMessage();
  const moveToAccount = useMoveMessageToAccount();
  const summarize = useSummarizeMessage();
  const categorize = useCategorizeMessage();
  const bulkMarkRead = useBulkMarkRead();
  const bulkStar = useBulkStar();
  const bulkMove = useBulkMove();
  const bulkDelete = useBulkDelete();
  const discardDraft = useDiscardDraft();
  const moveByFilter = useBulkMoveByFilter();
  const flagByFilter = useBulkFlagByFilter();
  const createFolder = useCreateFolder();
  const renameFolder = useRenameFolder();
  const deleteFolder = useDeleteFolder();
  const unsubscribe = useUnsubscribeMessage();
  const addCalendarInvite = useAddCalendarInvite();
  // Is the open message's sender already a CRM contact? Drives the reader's
  // "In Contacts" vs "Add to contacts" affordance (and prevents duplicate adds).
  const senderContact = useContactByEmail(openMessage?.fromAddress, !!openMessage?.fromAddress);
  const reportSpam = useReportSpam();
  const notSpam = useNotSpam();

  const archiveFolderId = folders?.find((f) => f.systemRole === 'archive')?.folderId;
  const trashFolderId = folders?.find((f) => f.systemRole === 'trash')?.folderId;
  const spamFolderId = folders?.find((f) => f.systemRole === 'spam')?.folderId;

  // id → message, for resolving the current selection into message objects
  // (so a bulk action can capture each row's before-state for undo).
  const messageById = useMemo(() => {
    const map = new Map<string, EmailMessageDto>();
    for (const m of messages ?? []) map.set(m.messageId, m);
    return map;
  }, [messages]);

  const toLite = (m: {
    messageId: string;
    folderId: string;
    isRead: boolean;
    isStarred: boolean;
  }): MsgLite => ({
    messageId: m.messageId,
    folderId: m.folderId,
    isRead: m.isRead,
    isStarred: m.isStarred,
  });

  const groupByFolder = (msgs: MsgLite[]): Map<string, string[]> => {
    const m = new Map<string, string[]>();
    for (const x of msgs) {
      const arr = m.get(x.folderId);
      if (arr) arr.push(x.messageId);
      else m.set(x.folderId, [x.messageId]);
    }
    return m;
  };
  // Undo helper: move each captured message back to the folder it came from.
  const moveBackByGroups = (groups: Map<string, string[]>) => {
    for (const [from, gids] of groups) {
      bulkMove.mutate({ messageIds: gids, target: { targetFolderId: from } });
    }
  };

  // Toast with an inline Undo for actions that remove a message from view
  // (move / archive / delete) — undoes the most recent recorded action.
  const toastUndo = (description: string) => {
    toast({
      description,
      action: (
        <ToastAction altText="Undo" onClick={() => history.undo()}>
          Undo
        </ToastAction>
      ),
    });
  };

  // ── Undoable actions ───────────────────────────────────────────────────────
  // Each runs the optimistic mutation now and records an inverse on the history
  // stack. They take captured `MsgLite[]` (id + folder + flags at action time).
  const actMarkRead = (msgs: MsgLite[], value: boolean) => {
    const ids = msgs.map((m) => m.messageId);
    if (ids.length === 0) return;
    const changed = msgs.filter((m) => m.isRead !== value).map((m) => m.messageId);
    bulkMarkRead.mutate({ messageIds: ids, value });
    if (changed.length === 0) return;
    history.record({
      label: value ? `Marked ${changed.length} read` : `Marked ${changed.length} unread`,
      undo: () => bulkMarkRead.mutate({ messageIds: changed, value: !value }),
      redo: () => bulkMarkRead.mutate({ messageIds: changed, value }),
    });
  };

  const actStar = (msgs: MsgLite[], value: boolean) => {
    const ids = msgs.map((m) => m.messageId);
    if (ids.length === 0) return;
    const changed = msgs.filter((m) => m.isStarred !== value).map((m) => m.messageId);
    bulkStar.mutate({ messageIds: ids, value });
    if (changed.length === 0) return;
    history.record({
      label: value ? `Starred ${changed.length}` : `Unstarred ${changed.length}`,
      undo: () => bulkStar.mutate({ messageIds: changed, value: !value }),
      redo: () => bulkStar.mutate({ messageIds: changed, value }),
    });
  };

  const actMove = (msgs: MsgLite[], targetFolderId: string, label: string) => {
    const items = msgs.filter((m) => m.folderId !== targetFolderId);
    if (items.length === 0) return;
    const ids = items.map((m) => m.messageId);
    const groups = groupByFolder(items);
    bulkMove.mutate({ messageIds: ids, target: { targetFolderId } });
    history.record({
      label,
      undo: () => moveBackByGroups(groups),
      redo: () => bulkMove.mutate({ messageIds: ids, target: { targetFolderId } }),
    });
    toastUndo(label);
  };

  // Cross-account move by system role — the server resolves the role to each
  // message's own account folder. This is how "Move to" works in the unified
  // view (where a single folder id can't span accounts).
  const actMoveToRole = (msgs: MsgLite[], role: MoveRole, label: string) => {
    if (msgs.length === 0) return;
    const ids = msgs.map((m) => m.messageId);
    const groups = groupByFolder(msgs);
    bulkMove.mutate({ messageIds: ids, target: { targetSystemRole: role } });
    history.record({
      label,
      undo: () => moveBackByGroups(groups),
      redo: () => bulkMove.mutate({ messageIds: ids, target: { targetSystemRole: role } }),
    });
    toastUndo(label);
  };

  const actArchive = (msgs: MsgLite[]) => {
    // Skip messages already in (this account's) archive; cross-account ones are
    // resolved per-account on the server.
    const items = archiveFolderId ? msgs.filter((m) => m.folderId !== archiveFolderId) : msgs;
    if (items.length === 0) return;
    const ids = items.map((m) => m.messageId);
    const groups = groupByFolder(items);
    bulkMove.mutate({ messageIds: ids, target: { targetSystemRole: 'archive' } });
    const label = ids.length > 1 ? `Archived ${ids.length} messages` : 'Archived message';
    history.record({
      label,
      undo: () => moveBackByGroups(groups),
      redo: () => bulkMove.mutate({ messageIds: ids, target: { targetSystemRole: 'archive' } }),
    });
    toastUndo(label);
  };

  // Delete = move to Trash (recoverable). A message already in Trash is deleted
  // permanently (Empty-Trash-style, not undoable).
  const actTrash = (msgs: MsgLite[]) => {
    if (msgs.length === 0) return;
    const inTrash = trashFolderId ? msgs.filter((m) => m.folderId === trashFolderId) : [];
    const toTrash = trashFolderId ? msgs.filter((m) => m.folderId !== trashFolderId) : msgs;
    if (inTrash.length > 0) bulkDelete.mutate(inTrash.map((m) => m.messageId));
    if (toTrash.length > 0) {
      const ids = toTrash.map((m) => m.messageId);
      const groups = groupByFolder(toTrash);
      bulkMove.mutate({ messageIds: ids, target: { targetSystemRole: 'trash' } });
      const label = ids.length > 1 ? `Deleted ${ids.length} messages` : 'Deleted message';
      history.record({
        label,
        undo: () => moveBackByGroups(groups),
        redo: () => bulkMove.mutate({ messageIds: ids, target: { targetSystemRole: 'trash' } }),
      });
      toastUndo(`${label} — moved to Trash`);
    }
  };

  // Report as spam: blacklist the sender + file to Spam. Not undoable via the
  // history stack (use "Not spam" to reverse), so we close the reader after.
  const actReportSpam = (accountId: string, messageId: string) => {
    reportSpam.mutate({ accountId, messageIds: [messageId] });
    setActiveMessageId(null);
  };

  // Not spam: whitelist the sender + move this message (and the sender's other
  // Spam mail) back to Inbox.
  const actNotSpam = (accountId: string, messageId: string) => {
    notSpam.mutate({ accountId, messageId });
    setActiveMessageId(null);
  };

  // Run a configured swipe action on a row (mobile). Sets `swipedRef` so the
  // row's tap handler skips the synthesized click that follows a swipe.
  const performSwipe = (m: EmailMessageDto, action: SwipeAction) => {
    if (action === 'none') return;
    swipedRef.current = true;
    const closesIfOpen = () => {
      if (activeMessageId === m.messageId) setActiveMessageId(null);
    };
    const lite = toLite(m);
    if (action === 'trash') {
      actTrash([lite]);
      closesIfOpen();
    } else if (action === 'archive') {
      actArchive([lite]);
      closesIfOpen();
    } else if (action === 'read') {
      actMarkRead([lite], !m.isRead);
    } else if (action === 'star') {
      actStar([lite], !m.isStarred);
    }
  };

  // ── Selection helpers ──────────────────────────────────────────────────────
  const selectedCount = selectedIds.size;
  const allShownSelected =
    !!messages && messages.length > 0 && messages.every((m) => selectedIds.has(m.messageId));
  const toggleSelect = (id: string) => {
    // Touching a single row means the selection is no longer the whole folder.
    setSelectAllInFolder(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionAnchorId(null);
    setSelectAllInFolder(false);
  };
  const selectAllShown = () => setSelectedIds(new Set((messages ?? []).map((m) => m.messageId)));
  // Extend the selection from the anchor to `id` inclusive — the contiguous run a
  // desktop mail client selects on Shift-click. With no usable anchor (none set,
  // or it scrolled out of the current list) it just adds `id` and anchors there.
  // The anchor stays put across Shift-clicks so the user can re-range from the
  // same start. Shared by Shift-click on a checkbox and on a row body.
  const selectRangeToAnchor = (id: string) => {
    setSelectAllInFolder(false);
    const list = messages ?? [];
    const targetIdx = list.findIndex((m) => m.messageId === id);
    const anchorIdx = selectionAnchorId
      ? list.findIndex((m) => m.messageId === selectionAnchorId)
      : -1;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (anchorIdx !== -1 && targetIdx !== -1) {
        const [lo, hi] = anchorIdx <= targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
        for (let i = lo; i <= hi; i++) next.add(list[i].messageId);
      } else {
        next.add(id);
      }
      return next;
    });
    if (anchorIdx === -1) setSelectionAnchorId(id);
  };
  // Click a row's checkbox to toggle it (and make it the anchor); Shift-click
  // extends the selection to it instead.
  const handleSelectClick = (id: string, shiftKey: boolean) => {
    if (shiftKey) {
      selectRangeToAnchor(id);
      return;
    }
    toggleSelect(id);
    setSelectionAnchorId(id);
  };
  // Toggle selection mode on/off; turning it off drops the current selection.
  const toggleSelectionMode = () => {
    setSelectionMode((on) => {
      if (on) clearSelection();
      return !on;
    });
  };
  // Resolve the current selection to captured message objects (for undo).
  const selectedMsgsLite = (): MsgLite[] =>
    [...selectedIds]
      .map((id) => messageById.get(id))
      .filter((m): m is EmailMessageDto => !!m)
      .map(toLite);
  // Fire a bulk action over the current selection. `closesOpen` also closes the
  // reading pane if the open message is part of the set (it's about to leave the
  // view). `keepSelection` preserves the selection afterwards — used by the
  // in-place flag actions (read/unread/star) where the rows stay in view, so the
  // user can chain actions on the same set. Move/archive/delete remove the rows
  // from the view, so they clear the selection.
  const runBulk = (
    actFn: (msgs: MsgLite[]) => void,
    opts?: { closesOpen?: boolean; keepSelection?: boolean },
  ) => {
    const msgs = selectedMsgsLite();
    if (msgs.length === 0) return;
    if (opts?.closesOpen && activeMessageId && selectedIds.has(activeMessageId)) {
      setActiveMessageId(null);
    }
    actFn(msgs);
    if (!opts?.keepSelection) clearSelection();
  };

  // ── Whole-folder ("select all in folder") bulk actions ─────────────────────
  // The scope a by-filter bulk action runs over — the SAME dimensions the list
  // query uses, so it hits exactly the set the user sees. Null when there's no
  // single folder to act on: search results span folders, and a sender or
  // category drill-down is a narrowing the by-filter path can't express (so
  // "select all shown" acts on the loaded set, without whole-folder escalation).
  const bulkFilterScope: EmailBulkFilterScope | null =
    hasQuery || senderFilter || categoryFilter !== undefined || labelFilter !== undefined
      ? null
      : isMerged
        ? { folderSystemRole: 'inbox', screener: anyScreenerEnabled, ...quickFilters }
        : contentAccountId && contentFolderId
          ? {
              accountId: contentAccountId,
              folderId: contentFolderId,
              screener: !isUnified && screenerEnabled && viewingInbox,
              ...quickFilters,
            }
          : null;

  // Fire a whole-folder MOVE (archive / delete-to-trash / move-to-folder) over
  // the entire matching set server-side. Closes the reading pane (rows leave the
  // view) and exits selection mode. `verb` is just for the result toast.
  const runWholeFolderMove = (
    target: Parameters<typeof moveByFilter.mutate>[0]['target'],
    verb: string,
  ) => {
    if (!bulkFilterScope) return;
    setActiveMessageId(null);
    moveByFilter.mutate(
      { scope: bulkFilterScope, target },
      {
        onSuccess: (res) =>
          toast({
            description:
              `${res.moved.toLocaleString()} message${res.moved === 1 ? '' : 's'} ${verb}` +
              (res.capped ? ' — more remain, run again to finish.' : ''),
          }),
      },
    );
    clearSelection();
    setSelectionMode(false);
  };

  // Run the pending (confirmed) whole-folder destructive action.
  const executeWholeFolder = () => {
    const act = confirmWholeFolder;
    setConfirmWholeFolder(null);
    if (!act) return;
    if (act.kind === 'archive') runWholeFolderMove({ targetSystemRole: 'archive' }, 'archived');
    else if (act.kind === 'trash') runWholeFolderMove({ targetSystemRole: 'trash' }, 'deleted');
    else runWholeFolderMove({ targetFolderId: act.targetFolderId }, `moved to ${act.folderName}`);
  };

  // Whole-folder read/star — non-destructive, fired directly (no confirm); the
  // rows stay in view so the selection is kept.
  const runWholeFolderFlag = (field: 'read' | 'star', value: boolean) => {
    if (!bulkFilterScope) return;
    flagByFilter.mutate(
      { scope: bulkFilterScope, field, value },
      {
        onSuccess: (res) =>
          toast({
            description:
              res.updated > 0
                ? `${res.updated.toLocaleString()} message${res.updated === 1 ? '' : 's'} updated` +
                  (res.capped ? ' — more remain, run again to finish.' : '')
                : 'No messages needed changing.',
          }),
      },
    );
  };

  // ── Per-folder actions (sidebar ⋯ menu) ────────────────────────────────────
  // Mark every message in a specific folder read/unread (by filter).
  const markFolderRead = (folderId: string, value: boolean) => {
    if (!accountId) return;
    flagByFilter.mutate(
      { scope: { accountId, folderId }, field: 'read', value },
      {
        onSuccess: (res) =>
          toast({
            description:
              res.updated > 0
                ? `${res.updated.toLocaleString()} message${res.updated === 1 ? '' : 's'} marked ${
                    value ? 'read' : 'unread'
                  }`
                : 'No messages needed changing.',
          }),
      },
    );
  };
  // Empty a folder (confirmed): Trash purges permanently; any other folder moves
  // everything to Trash (recoverable).
  const emptyFolderConfirmed = () => {
    const f = confirmEmptyFolder;
    setConfirmEmptyFolder(null);
    if (!f || !accountId) return;
    setActiveMessageId(null);
    if (f.role === 'trash') {
      emptyTrash.mutate(accountId);
      return;
    }
    moveByFilter.mutate(
      { scope: { accountId, folderId: f.folderId }, target: { targetSystemRole: 'trash' } },
      {
        onSuccess: (res) =>
          toast({
            description:
              `${res.moved.toLocaleString()} message${res.moved === 1 ? '' : 's'} moved to Trash` +
              (res.capped ? ' — more remain, run again to finish.' : ''),
          }),
      },
    );
  };
  // Submit the shared New-folder / Rename dialog.
  const submitFolderName = (name: string) => {
    const d = folderNameDialog;
    setFolderNameDialog(null);
    if (!d || !accountId) return;
    if (d.mode === 'create')
      createFolder.mutate({ accountId, name, parentFolderId: d.parentFolderId });
    else renameFolder.mutate({ folderId: d.folderId, accountId, name });
  };
  // Delete a custom folder (confirmed). If it's the folder in view, fall back to
  // the default (inbox) so we're not left pointed at a folder that's gone.
  const deleteFolderConfirmed = () => {
    const f = confirmDeleteFolder;
    setConfirmDeleteFolder(null);
    if (!f || !accountId) return;
    if (activeFolderId === f.folderId) {
      setActiveFolderId(null);
      setActiveMessageId(null);
    }
    deleteFolder.mutate({ folderId: f.folderId, accountId });
  };

  // Switch the active folder. A global SEARCH spans every folder, so clicking a
  // folder while searching would appear to do nothing — clear the query so the
  // folder's own contents show. A FILTER re-applies to the newly selected
  // folder, so it's left on as the user traverses folders.
  const selectFolder = (folderId: string) => {
    setScreenerView(false);
    setActiveFolderId(folderId);
    setActiveMessageId(null);
    setSenderFilter(null);
    if (searchActive) setSearchTerm('');
  };

  // Open the dedicated Screener inbox (held mail). Single-account: this account's
  // screener; unified: All Screened across every account. Clears folder focus.
  const openScreener = () => {
    setScreenerView(true);
    setActiveFolderId(null);
    setUnifiedFocus(null);
    setActiveMessageId(null);
    setSenderFilter(null);
    setSearchTerm('');
    if (isMobile) setMobileSidebarOpen(false);
  };

  // From the "All accounts" sidebar, focus a single account's folder — the list
  // shows just that folder while the merged view (and its sidebar) stays put.
  const focusUnifiedFolder = (accountId: string, folderId: string) => {
    setScreenerView(false);
    setUnifiedFocus({ accountId, folderId });
    setActiveMessageId(null);
    setSearchTerm('');
    setSenderFilter(null);
    if (isMobile) setMobileSidebarOpen(false);
  };

  // Back to the merged all-inboxes list (the "All inboxes" button).
  const showAllInboxes = () => {
    setScreenerView(false);
    setUnifiedFocus(null);
    setActiveMessageId(null);
    setSearchTerm('');
    setSenderFilter(null);
    if (isMobile) setMobileSidebarOpen(false);
  };

  // ── Drag a message onto a folder to move (or Ctrl/⌘-drag to copy) it ──
  // Any folder other than the one the message is already in is a valid target:
  // same account → a plain folder move; different account → a cross-account
  // move/copy (fetch + APPEND + delete-from-source, handled server-side).
  const canDropOnFolder = (folder: EmailFolderDto) =>
    draggingMessage != null && draggingMessage.folderId !== folder.folderId;

  const handleDropOnFolder = (folder: EmailFolderDto, opts: { copy: boolean }) => {
    const dragged = draggingMessage;
    setDraggingMessage(null);
    if (!dragged || dragged.folderId === folder.folderId) return;

    if (dragged.accountId === folder.accountId) {
      // Same account: Ctrl/⌘-drag copies (IMAP COPY, original stays); a plain
      // drag moves (the row leaves this folder).
      if (opts.copy) {
        copyMessage.mutate({
          messageId: dragged.messageId,
          targetFolderId: folder.folderId,
          accountId: folder.accountId,
        });
        toast({ description: `Copying to ${folder.name}` });
        return;
      }
      if (activeMessageId === dragged.messageId) setActiveMessageId(null);
      moveMessage.mutate({ messageId: dragged.messageId, targetFolderId: folder.folderId });
      toast({ description: `Moved to ${folder.name}` });
      return;
    }

    // Different account: copy the bytes into the target, and (move mode) drop the
    // original to its source Trash. Reachable-now, so surface failures.
    const targetAddress = accountAddressById.get(folder.accountId) ?? 'the other account';
    if (!opts.copy && activeMessageId === dragged.messageId) setActiveMessageId(null);
    moveToAccount.mutate(
      {
        messageId: dragged.messageId,
        targetAccountId: folder.accountId,
        targetFolderId: folder.folderId,
        mode: opts.copy ? 'copy' : 'move',
      },
      {
        onError: () =>
          toast({
            variant: 'destructive',
            description: `Couldn't ${opts.copy ? 'copy' : 'move'} to ${targetAddress} — the message was left where it was.`,
          }),
      },
    );
    toast({
      description: `${opts.copy ? 'Copying' : 'Moving'} to ${folder.name} · ${targetAddress}`,
    });
  };

  // Enter/leave a grouping overview ("By Sender" / "By Category"). Clicking the
  // active mode's chip returns to the flat list. Entering closes the reading
  // pane, any drill-down, and selection mode (the cards take over the main area;
  // selection is a list concept).
  const toggleGroupMode = (mode: 'sender' | 'category') => {
    setGroupMode((cur) => {
      const next = cur === mode ? 'none' : mode;
      if (next !== 'none') {
        setActiveMessageId(null);
        setSenderFilter(null);
        setCategoryFilter(undefined);
        if (selectionMode) {
          clearSelection();
          setSelectionMode(false);
        }
      }
      return next;
    });
  };

  // Click a sender card → leave the overview and narrow the regular list to that
  // sender. Filters the already-loaded folder view in memory (exact from-address,
  // uncapped) rather than routing through the search endpoint — so a big sender
  // shows ALL of its messages that are already loaded, not the search route's
  // first 50/200. Clears any active search / category drill-down (all mutually
  // exclusive).
  const openSender = (senderAddress: string) => {
    setGroupMode('none');
    setSearchTerm('');
    setCategoryFilter(undefined);
    setLabelFilter(undefined);
    setSenderFilter(senderAddress.toLowerCase());
    setActiveMessageId(null);
  };

  // Click a category card → leave the overview and narrow the regular list to
  // that category (server-side, since category is a thread attribute). null =
  // the Uncategorized card. Clears any active search / sender drill-down.
  const openCategory = (categoryId: string | null) => {
    setGroupMode('none');
    setSearchTerm('');
    setSenderFilter(null);
    setLabelFilter(undefined);
    setCategoryFilter(categoryId ?? 'uncategorized');
    setActiveMessageId(null);
  };

  // Dismiss the category drill-down chip → back to the full folder list.
  const clearCategoryFilter = () => setCategoryFilter(undefined);

  // Click a label chip → narrow the list to that label (server-side). Clears any
  // active search / sender / category drill-down (they're mutually exclusive).
  const openLabel = (labelId: string) => {
    setGroupMode('none');
    setSearchTerm('');
    setSenderFilter(null);
    setCategoryFilter(undefined);
    setLabelFilter(labelId);
    setActiveMessageId(null);
  };

  // Dismiss the label drill-down chip → back to the full folder list.
  const clearLabelFilter = () => setLabelFilter(undefined);

  const openCompose = (draft: ComposeInitialDraft) => {
    setComposeDraft(draft);
    setComposeOpen(true);
  };

  // Plain-text body of a message (prefers text; strips HTML as a fallback) —
  // shared by the reading pane and the reply/forward quoting below.
  const messageBodyText = (m: EmailMessageDetailDto): string =>
    m.bodyText ??
    (m.bodyHtml
      ? m.bodyHtml
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
      : '');

  // The original body as HTML for quoting: prefer the stored HTML so formatting
  // survives (the composer's TipTap schema drops anything unsafe on parse), else
  // escape the plain text. Remote images are stripped so quoting a marketing mail
  // doesn't fire its tracking pixels inside the composer.
  const messageBodyHtml = (m: EmailMessageDetailDto): string => {
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (m.bodyHtml) return stripQuotedRemoteImages(m.bodyHtml);
    const text = m.bodyText ?? '';
    return (
      text
        .split(/\n{2,}/)
        .map((para) => `<p>${esc(para).replace(/\n/g, '<br>')}</p>`)
        .join('') || '<p></p>'
    );
  };

  const quoteOriginalHtml = (m: EmailMessageDetailDto): string => {
    const order = userSettings.replyQuoteOrder;
    if (order === 'none') return '';
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const who = m.fromName || m.fromAddress;
    const when = m.receivedAt ? new Date(m.receivedAt).toLocaleString() : '';
    const header = when ? `On ${esc(when)}, ${esc(who)} wrote:` : `${esc(who)} wrote:`;
    const quote = `<p>${header}</p><blockquote>${messageBodyHtml(m)}</blockquote>`;
    // 'above' (default): two blank lines on top for the reply, then the quote
    // below (inline reply above the previous message) — the cursor lands in the
    // first blank line. 'below': quote on top (bottom-posting).
    return order === 'below' ? `${quote}<p></p>` : `<p></p><p></p>${quote}`;
  };

  // Reply (all=false) or Reply All (all=true). Reply All adds the original To
  // recipients (minus the sender and the account's own address) and carries the
  // original Cc, de-duplicated.
  const buildReplyDraft = (m: EmailMessageDetailDto, all: boolean): ComposeInitialDraft => {
    const self = (accountAddressById.get(m.accountId) ?? '').toLowerCase();
    const toList: { email: string; name?: string | null }[] = [
      { email: m.fromAddress, name: m.fromName },
    ];
    const ccList: { email: string; name?: string | null }[] = [];
    if (all) {
      const seen = new Set([m.fromAddress.toLowerCase(), self]);
      for (const a of m.toAddresses) {
        const lc = a.email.toLowerCase();
        if (seen.has(lc)) continue;
        seen.add(lc);
        toList.push(a);
      }
      for (const a of m.ccAddresses ?? []) {
        const lc = a.email.toLowerCase();
        if (seen.has(lc)) continue;
        seen.add(lc);
        ccList.push(a);
      }
    }
    return {
      to: toList.map((a) => a.email).join(', '),
      cc: ccList.length ? ccList.map((a) => a.email).join(', ') : undefined,
      subject: /^re:/i.test(m.subject) ? m.subject : `Re: ${m.subject}`,
      inReplyToMessageId: m.messageId,
      bodyHtml: quoteOriginalHtml(m),
    };
  };

  const buildForwardDraft = (
    m: EmailMessageDetailDto,
    atts: EmailMailboxAttachmentDto[] | undefined,
  ): ComposeInitialDraft => {
    const lines = [
      '---------- Forwarded message ----------',
      `From: ${m.fromName ? `${m.fromName} <${m.fromAddress}>` : m.fromAddress}`,
      m.receivedAt ? `Date: ${new Date(m.receivedAt).toLocaleString()}` : '',
      `Subject: ${m.subject || '(no subject)'}`,
      `To: ${m.toAddresses.map((a) => a.name || a.email).join(', ')}`,
    ].filter(Boolean);
    // Honor the "Don't quote the original" preference on forwards too — omit the
    // forwarded header + body, leaving a blank compose area.
    const quoteless = userSettings.replyQuoteOrder === 'none';
    return {
      subject: /^fwd:/i.test(m.subject) ? m.subject : `Fwd: ${m.subject}`,
      body: quoteless ? '' : `\n\n${lines.join('\n')}\n\n${messageBodyText(m)}`,
      // Forwarded attachments are already Vault docs — re-attach by documentId.
      attachments: (atts ?? [])
        .filter((a): a is typeof a & { documentId: string } => !!a.documentId)
        .map((a) => ({
          documentId: a.documentId,
          fileName: a.filename,
          mimeType: a.mimeType,
          sizeBytes: a.sizeBytes,
          s3Key: '',
          downloadUrl: '',
        })),
    };
  };

  // Resume a Drafts-folder message in the composer (instead of the read pane).
  // Fetches the full body + recipients + attachments, then opens the composer
  // carrying `draftMessageId` so saving/sending supersedes this draft.
  const openDraftForEditing = async (messageId: string) => {
    const joinEmails = (arr?: { email: string }[]) =>
      arr && arr.length > 0 ? arr.map((a) => a.email).join(', ') : undefined;
    try {
      const [detail, attachmentsRes] = await Promise.all([
        api.getEmailMailboxMessage(messageId),
        api.listEmailMailboxMessageAttachments(messageId),
      ]);
      // Inline (CID) images live in the body, not the attachment tray. Rehydrate
      // each `cid:<id>` back to a fresh preview URL + `data-doc-id` so the editor
      // shows it and a re-save re-attaches it as a CID part (see extractInlineImages).
      const inlineAtts = attachmentsRes.attachments.filter(
        (a) => a.isInline && a.contentId && a.documentId,
      );
      let bodyHtml = detail.bodyHtml ?? undefined;
      if (bodyHtml && inlineAtts.length > 0) {
        const resolved = await Promise.all(
          inlineAtts.map(async (a) => {
            try {
              const { url } = await api.getEmailMailboxAttachmentDownloadUrl(
                messageId,
                a.attachmentId,
                'inline',
              );
              return { documentId: a.documentId!, contentId: a.contentId!, url };
            } catch {
              return null;
            }
          }),
        );
        bodyHtml = rehydrateInlineImages(
          bodyHtml,
          resolved.filter((r) => r !== null),
        );
      }
      setComposeDraftLite(toLite(detail));
      setComposeDraft({
        to: joinEmails(detail.toAddresses),
        cc: joinEmails(detail.ccAddresses),
        bcc: joinEmails(detail.bccAddresses),
        subject: detail.subject,
        // Prefer the stored HTML so a formatted draft resumes with formatting;
        // bodyText is the plain-text fallback for older text-only drafts.
        bodyHtml,
        body: detail.bodyText ?? '',
        inReplyToMessageId: detail.inReplyTo ?? undefined,
        draftMessageId: messageId,
        attachments: attachmentsRes.attachments
          .filter((a): a is typeof a & { documentId: string } => !!a.documentId && !a.isInline)
          .map((a) => ({
            documentId: a.documentId,
            fileName: a.filename,
            mimeType: a.mimeType,
            sizeBytes: a.sizeBytes,
            s3Key: '',
            downloadUrl: '',
          })),
      });
      setComposeOpen(true);
    } catch {
      toast({
        title: 'Couldn’t open draft',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const sortedFolders = useMemo(() => sortFolders(folders ?? []), [folders]);
  // Depth-annotated, collapse-aware sidebar tree for the single-account view.
  // (`sortedFolders` stays the flat list for the move menus / drag-drop lookups.)
  const folderTree = useMemo(
    () => buildFolderTree(folders ?? [], collapsedFolders.isCollapsed),
    [folders, collapsedFolders.isCollapsed],
  );

  // "Move to" menu options. Single-account: this account's folders (by id).
  // Unified: system-role destinations (by `role:<role>`), since a folder id
  // can't span accounts — see SYSTEM_MOVE_ROLES.
  const moveDestinations = useMemo(
    () =>
      isUnified
        ? SYSTEM_MOVE_ROLES.map((r) => ({ key: `role:${r.role}`, label: r.label }))
        : sortedFolders.map((f) => ({ key: f.folderId, label: f.name })),
    [isUnified, sortedFolders],
  );

  // Resolve a "Move to" selection (a folder id, or `role:<role>` in the unified
  // view) into the right move action for a set of messages.
  const moveMsgsTo = (msgs: MsgLite[], key: string) => {
    if (key.startsWith('role:')) {
      const role = key.slice('role:'.length) as MoveRole;
      const label = SYSTEM_MOVE_ROLES.find((r) => r.role === role)?.label ?? 'folder';
      actMoveToRole(msgs, role, `Moved to ${label}`);
    } else {
      const dest = sortedFolders.find((f) => f.folderId === key);
      actMove(msgs, key, `Moved to ${dest?.name ?? 'folder'}`);
    }
  };

  // Auto-sync on open and whenever the active view/folder changes, so a freshly
  // opened mailbox pulls new mail immediately instead of waiting for the periodic
  // (2-min) tick. A short per-target freshness guard (30s) avoids re-pulling on
  // every click or accounts refetch. Single-account view syncs the active folder;
  // the unified view syncs every connected account (mirroring "Sync all"), since
  // it spans them all — previously it did nothing on open and stayed stale until
  // the first background tick.
  const lastFolderSyncRef = useRef<Map<string, number>>(new Map());
  const syncFolderMutate = syncFolder.mutate;
  const syncAccountMutate = sync.mutate;
  // True (and records the time) when `key` hasn't pulled in the last 30s. Shared
  // by the on-open sync, the periodic tick, and the focus/visibility pull below
  // so none of them re-pull the same target within 30s of another — rapid tab
  // switching or a folder reopen can't hammer the IMAP server.
  const dueForSync = useCallback((key: string): boolean => {
    const now = Date.now();
    if (now - (lastFolderSyncRef.current.get(key) ?? 0) < 30_000) return false;
    lastFolderSyncRef.current.set(key, now);
    return true;
  }, []);
  useEffect(() => {
    if (isUnified) {
      for (const a of accounts ?? []) {
        if (dueForSync(`acct:${a.accountId}`)) syncAccountMutate(a.accountId);
      }
    } else if (accountId && inboxFolderId) {
      if (dueForSync(`${accountId}:${inboxFolderId}`)) {
        syncFolderMutate({ accountId, folderId: inboxFolderId });
      }
    }
  }, [
    accountId,
    inboxFolderId,
    isUnified,
    accounts,
    syncFolderMutate,
    syncAccountMutate,
    dueForSync,
  ]);

  // Periodic background refresh: while the mailbox is open, pull new mail every
  // two minutes so an idle inbox stays current without a manual "Sync". The tick
  // is kept in a ref (updated each render) so the interval installs once yet
  // always runs the latest closure. Pauses while the
  // browser tab is hidden, and skips a tick while a sync is already in flight.
  // Single-account view refreshes the active folder (forward-only, light); the
  // unified view refreshes every connected account.
  const autoSyncTickRef = useRef<() => void>(() => undefined);
  autoSyncTickRef.current = () => {
    if (typeof document !== 'undefined' && document.hidden) return;
    if (sync.isPending || syncFolder.isPending || syncStream.syncing) return;
    if (isUnified) {
      for (const a of accounts ?? []) {
        if (dueForSync(`acct:${a.accountId}`)) sync.mutate(a.accountId);
      }
    } else if (accountId && inboxFolderId) {
      if (dueForSync(`${accountId}:${inboxFolderId}`)) {
        syncFolder.mutate({ accountId, folderId: inboxFolderId });
      }
    }
  };
  useEffect(() => {
    const id = setInterval(() => autoSyncTickRef.current(), AUTO_SYNC_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Pull immediately when the user returns to the tab/window. The periodic tick
  // early-returns while the tab is hidden, so ticks that fell due in the
  // background never ran — without this, returning to the mailbox shows mail as
  // stale as the last pre-hide tick (up to a full interval) until the next one
  // fires. React Query's refetchOnWindowFocus only re-reads our own API (the DB
  // rows the 5-min server cron wrote); it does not pull from the IMAP server.
  // This does — reusing the tick (and its shared 30s freshness guard, so
  // flicking between tabs can't hammer IMAP). The tick's own `document.hidden`
  // check makes the "tab just became hidden" visibilitychange a no-op.
  useEffect(() => {
    const pullOnReturn = () => autoSyncTickRef.current();
    window.addEventListener('focus', pullOnReturn);
    document.addEventListener('visibilitychange', pullOnReturn);
    return () => {
      window.removeEventListener('focus', pullOnReturn);
      document.removeEventListener('visibilitychange', pullOnReturn);
    };
  }, []);

  // Reset the "no older" backfill state when the view changes — and drop the
  // selection, since the visible set is different now.
  const backfillReset = backfill.reset;
  useEffect(() => {
    setConfirmEmptyTrash(false);
    setSelectedIds(new Set());
    setSelectAllInFolder(false);
    backfillReset();
  }, [
    accountId,
    inboxFolderId,
    isUnified,
    unifiedFocus,
    searchTerm,
    searchFieldsArr,
    searchMode,
    senderFilter,
    categoryFilter,
    labelFilter,
    filterUnread,
    filterStarred,
    filterAttachments,
    backfillReset,
  ]);

  // Focus is a unified-only concept — clear it whenever we drop out of the
  // "All accounts" view (e.g. an account is disconnected, leaving <2).
  useEffect(() => {
    if (!isUnified) setUnifiedFocus(null);
  }, [isUnified]);

  const shownCount = messages?.length ?? 0;

  // Header count line. When the view is narrowed (a quick filter on, or a search
  // active) the user wants to know how much the narrowing left of the whole —
  // "42 of 305 messages". A search across all folders has no single folder total
  // to compare against, so it shows just the result count.
  const isFiltering = filterUnread || filterStarred || filterAttachments;
  const folderTotal = activeFolder?.messageCount ?? 0;
  // A global search spans every folder, so there's no single-folder total to
  // compare against — show just the result count.
  const plural = (n: number) => (n === 1 ? '' : 's');
  const listCountLabel = (() => {
    if (screenerView) return `${shownCount.toLocaleString()} held`;
    if (searchActive) return `${shownCount.toLocaleString()} result${plural(shownCount)}`;
    if (isMerged) {
      // The merged inbox has no single folder total, so show how many are shown.
      return `${shownCount.toLocaleString()} message${plural(shownCount)}`;
    }
    // A focused folder (in "All accounts" mode) has a real total, like single-account.
    if (isFiltering || senderFilter || filterActive)
      return `${shownCount.toLocaleString()} of ${folderTotal.toLocaleString()} message${plural(folderTotal)}`;
    return `${folderTotal.toLocaleString()} message${plural(folderTotal)}`;
  })();

  // List-column heading. A global search spans everything ("Search · All mail");
  // a filter prefixes the current view ("Filter · Inbox"); otherwise it's the
  // view name (merged inboxes, or a folder + optional focused-account suffix).
  const viewHeading = (() => {
    if (screenerView) return isUnified ? 'All Screened' : 'Screener';
    if (searchActive) return 'Search · All mail';
    if (isMerged) return filterActive ? 'Filter · All inboxes' : 'All inboxes';
    const base = `${activeFolder?.name ?? 'Folder'}${
      isUnified && unifiedFocus ? ` · ${accountAddressById.get(unifiedFocus.accountId) ?? ''}` : ''
    }`;
    return filterActive ? `Filter · ${base}` : base;
  })();

  // Whether the folder holds more than the loaded window — the trigger for the
  // "Select all N in folder" affordance. Single-account knows the folder total;
  // the unified inbox has no single total, so a full window is the best signal
  // that more exist. Search results span folders, so the whole-folder path (and
  // its scope) doesn't apply there.
  const moreInFolderThanLoaded =
    !!bulkFilterScope &&
    !viewingTrash &&
    (isMerged ? shownCount >= MESSAGE_LIST_MAX : folderTotal > shownCount);
  // Label for the whole-folder count (best-effort: unknown in the merged view,
  // but a focused single folder has a real total).
  const wholeFolderCountLabel = isMerged ? '' : ` ${folderTotal.toLocaleString()}`;

  // The list shows every already-synced row for the current view up front (the
  // fetch is capped at MESSAGE_LIST_MAX). Older mail that's on the server but not
  // yet synced is a separate layer, reachable only by a single-account folder via
  // backfill until the folder is fully walked. The unified inbox has no
  // per-account backfill — it's all synced.
  // Older-mail backfill targets, resolved for the current scope. Only folders
  // with a server path can be backfilled.
  //  - "Load more"  → one older batch from the inbox(es)
  //  - "Load all"   → walk EVERY folder to the bottom
  // Both work in the single-account view (this account) and the unified "All
  // accounts" view (every connected account).
  type BackfillTarget = { accountId: string; folderId: string; name: string };
  const inboxBackfillTargets = useMemo<BackfillTarget[]>(() => {
    if (isMerged) {
      const out: BackfillTarget[] = [];
      for (const [accId, list] of foldersByAccount) {
        const inbox = list.find((f) => f.systemRole === 'inbox' && f.serverPath);
        if (inbox) out.push({ accountId: accId, folderId: inbox.folderId, name: inbox.name });
      }
      return out;
    }
    // Single-account OR a focused folder in "All accounts": "Load more" pulls
    // older mail from the exact folder on screen.
    return contentAccountId && contentFolderId && activeFolder?.serverPath
      ? [
          {
            accountId: contentAccountId,
            folderId: contentFolderId,
            name: activeFolder?.name ?? 'Inbox',
          },
        ]
      : [];
  }, [
    isMerged,
    foldersByAccount,
    contentAccountId,
    contentFolderId,
    activeFolder?.serverPath,
    activeFolder?.name,
  ]);

  const allBackfillTargets = useMemo<BackfillTarget[]>(() => {
    const out: BackfillTarget[] = [];
    const add = (accId: string, list: EmailFolderDto[]) => {
      // INBOX first — the user's most important folder downloads before the rest.
      const ordered = [...list].sort(
        (a, b) => Number(b.systemRole === 'inbox') - Number(a.systemRole === 'inbox'),
      );
      for (const f of ordered)
        if (f.serverPath) out.push({ accountId: accId, folderId: f.folderId, name: f.name });
    };
    if (isMerged) {
      for (const [accId, list] of foldersByAccount) add(accId, list);
    } else if (contentAccountId) {
      // Focused view walks the focused account's folders; single-account walks its own.
      add(
        contentAccountId,
        isUnified ? (foldersByAccount.get(contentAccountId) ?? []) : (folders ?? []),
      );
    }
    return out;
  }, [isMerged, isUnified, foldersByAccount, contentAccountId, folders]);

  // "Load more" = one JSON backfill batch per inbox (quick, incremental).
  // "Load all" = the STREAMED walk of every folder to the bottom, with live
  // per-message progress (see useBackfillAllStream). A ref guards "Load more"
  // against a double-run; the stream guards itself via `syncing`.
  const backfillAllStream = useBackfillAllStream();
  const [loadingMore, setLoadingMore] = useState(false);
  const backfillingRef = useRef(false);
  const backfillMutate = backfill.mutateAsync;
  const loadingBackfill = loadingMore || backfillAllStream.syncing;

  const loadMore = useCallback(async () => {
    if (backfillingRef.current || backfillAllStream.syncing || inboxBackfillTargets.length === 0)
      return;
    backfillingRef.current = true;
    setLoadingMore(true);
    try {
      for (const t of inboxBackfillTargets) await backfillMutate(t);
    } catch {
      // best-effort; the next sync / manual retry continues
    } finally {
      backfillingRef.current = false;
      setLoadingMore(false);
    }
  }, [backfillMutate, backfillAllStream.syncing, inboxBackfillTargets]);

  // Accounts to stream "Load all" over: this account (single), or every
  // connected account (unified "All accounts"). The stream walks all folders.
  const loadAllAccountIds = useMemo(
    () =>
      isMerged
        ? (accounts ?? []).map((a) => a.accountId)
        : contentAccountId
          ? [contentAccountId]
          : [],
    [isMerged, accounts, contentAccountId],
  );
  const loadAll = useCallback(() => {
    if (loadingBackfill || loadAllAccountIds.length === 0) return;
    if (isMerged) void backfillAllStream.startAll(loadAllAccountIds);
    else void backfillAllStream.start(loadAllAccountIds[0]);
  }, [loadingBackfill, loadAllAccountIds, isMerged, backfillAllStream]);

  // Load-controls bar, pinned at the BOTTOM of the message pane — a fixed
  // `shrink-0` row BELOW the scrolling list (not inside it), so it's always
  // visible and doesn't drift as mail loads. "Load more" pulls one older batch of
  // inbox mail; "Load all" walks every folder of the account (or every account,
  // in the unified view) to the bottom, showing live progress.
  const capReached = shownCount >= MESSAGE_LIST_MAX;
  // A folder is "done" once its history is walked to the bottom (backfillComplete).
  // Look it up from whichever source is populated for the current view — the
  // unified per-account map, or the single account's folder list. Unknown → treat
  // as NOT complete (show the control), so we never hide a real "more to load".
  const findFolderDto = (accountId: string, folderId: string) =>
    (foldersByAccount.get(accountId) ?? []).find((f) => f.folderId === folderId) ??
    (folders ?? []).find((f) => f.folderId === folderId);
  const inboxHasMore = inboxBackfillTargets.some(
    (t) => !findFolderDto(t.accountId, t.folderId)?.backfillComplete,
  );
  // Only offer Load controls while some in-scope folder still has older mail on
  // the server. When everything's been downloaded, they're just noise.
  const anyHasMore = allBackfillTargets.some(
    (t) => !findFolderDto(t.accountId, t.folderId)?.backfillComplete,
  );
  const loadControlsBar =
    !searchActive && shownCount > 0 && (anyHasMore || capReached) ? (
      <div className="shrink-0 border-t bg-background">
        {loadingBackfill ? (
          <div className="flex items-center justify-center gap-1.5 p-2.5 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {backfillAllStream.syncing
              ? backfillAllStream.progress
                ? `Downloading ${formatSyncProgress(backfillAllStream.progress)}`
                : 'Loading all messages…'
              : 'Loading more…'}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5 p-2.5">
            {anyHasMore && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs"
                  disabled={!inboxHasMore}
                  onClick={loadMore}
                >
                  Load more
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-xs"
                  onClick={loadAll}
                >
                  Load all messages
                </Button>
              </div>
            )}
            {backfillAllStream.error && (
              <p className="text-[11px] text-destructive">{backfillAllStream.error}</p>
            )}
            {capReached && (
              <p className="text-[11px] text-muted-foreground">
                Showing the first {MESSAGE_LIST_MAX.toLocaleString()} — search to find the rest.
              </p>
            )}
          </div>
        )}
      </div>
    ) : null;

  const downloadAttachment = async (attachmentId: string, filename: string) => {
    if (!activeMessageId) return;
    try {
      const { url } = await api.getEmailMailboxAttachmentDownloadUrl(activeMessageId, attachmentId);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast({
        title: 'Couldn’t download attachment',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────
  // Gmail-style. Disabled while a dialog is open or while typing in a field. The
  // handler is kept in a ref (updated each render) and bound once, so it always
  // sees the latest message list / open message without re-subscribing.
  const anyDialogOpen =
    composeOpen ||
    scheduledOpen ||
    snoozeOpen ||
    snoozedListOpen ||
    settingsOpen ||
    contactsOpen ||
    filtersOpen ||
    screenerOpen ||
    spamOpen ||
    vacationOpen ||
    addContactOpen;

  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = (e: KeyboardEvent) => {
    if (anyDialogOpen) return;
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName;
    const typing =
      tag === 'INPUT' ||
      tag === 'TEXTAREA' ||
      tag === 'SELECT' ||
      target?.isContentEditable === true;
    const k = e.key.toLowerCase();

    // Undo/redo — Cmd/Ctrl+Z and Cmd/Ctrl+Shift+Z (or Ctrl+Y). Skipped while
    // typing so the field's native text undo still works.
    if ((e.metaKey || e.ctrlKey) && !typing) {
      if (k === 'z' && !e.shiftKey) {
        if (history.canUndo) {
          e.preventDefault();
          history.undo();
        }
        return;
      }
      if ((k === 'z' && e.shiftKey) || k === 'y') {
        if (history.canRedo) {
          e.preventDefault();
          history.redo();
        }
        return;
      }
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (typing) return;

    // Navigation + global keys (no open message required).
    if (k === 'j' || k === 'k') {
      if (!messages || messages.length === 0) return;
      e.preventDefault();
      const idx = messages.findIndex((m) => m.messageId === activeMessageId);
      const nextIdx =
        k === 'j'
          ? Math.min((idx < 0 ? -1 : idx) + 1, messages.length - 1)
          : Math.max((idx < 0 ? 0 : idx) - 1, 0);
      const next = messages[nextIdx];
      if (next && next.messageId !== activeMessageId) {
        setActiveMessageId(next.messageId);
        summarize.reset();
        categorize.reset();
        if (!next.isRead) markRead.mutate({ messageId: next.messageId, value: true });
      }
      return;
    }
    if (k === '/') {
      e.preventDefault();
      searchInputRef.current?.focus();
      return;
    }
    if (e.key === 'Escape') {
      if (activeMessageId) {
        e.preventDefault();
        setActiveMessageId(null);
      }
      return;
    }

    // Archive / delete act on the current multi-selection when there is one
    // (same as the bulk bar buttons), otherwise on the open message. This is why
    // they're handled before the open-message guard below — a selection alone,
    // with nothing open, is still a valid target.
    const hasSelection = selectedIds.size > 0;
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (hasSelection) {
        e.preventDefault();
        runBulk((msgs) => actTrash(msgs), { closesOpen: true });
      } else if (openMessage) {
        e.preventDefault();
        actTrash([toLite(openMessage)]);
        setActiveMessageId(null);
      }
      return;
    }
    if (k === 'a' && !e.shiftKey) {
      if (hasSelection) {
        e.preventDefault();
        runBulk((msgs) => actArchive(msgs), { closesOpen: true });
      } else if (openMessage) {
        e.preventDefault();
        actArchive([toLite(openMessage)]);
        setActiveMessageId(null);
      }
      return;
    }

    // The remaining actions (reply / forward / star / mark-unread) operate on the
    // open message only.
    if (!openMessage) return;
    const openLite = toLite(openMessage);
    if (k === 'r') {
      e.preventDefault();
      openCompose(buildReplyDraft(openMessage, e.shiftKey));
      return;
    }
    if (k === 'f') {
      e.preventDefault();
      openCompose(buildForwardDraft(openMessage, openAttachments));
      return;
    }
    if (k === 's') {
      e.preventDefault();
      actStar([openLite], !openMessage.isStarred);
      return;
    }
    if (k === 'u') {
      e.preventDefault();
      actMarkRead([openLite], !openMessage.isRead);
    }
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keyHandlerRef.current(e);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (accountsLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  if (!accounts || accounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center gap-3 p-8">
        <Inbox className="h-10 w-10 text-muted-foreground/60" />
        <p className="font-medium">No mailboxes connected</p>
        <p className="text-sm text-muted-foreground">Connect an account to start reading mail.</p>
        <Button asChild>
          <Link to="../connections">Connect an account</Link>
        </Button>
      </div>
    );
  }

  const toggleThread = (threadId: string) =>
    setExpandedThreads((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) next.delete(threadId);
      else next.add(threadId);
      return next;
    });

  // A collapsed conversation row (only for threads with >1 message). Its chevron
  // expands/collapses the thread; its body opens the newest message.
  const renderThreadHeader = (group: EmailThreadGroup) => {
    const isExpanded = expandedThreads.has(group.threadId);
    const m = group.latest;
    return (
      <div className="group/mailrow flex items-stretch border-b hover:bg-accent/40">
        <button
          type="button"
          aria-label={isExpanded ? 'Collapse conversation' : 'Expand conversation'}
          aria-expanded={isExpanded}
          onClick={() => toggleThread(group.threadId)}
          className="flex shrink-0 items-center px-2 text-muted-foreground hover:text-foreground"
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <button
          onClick={() => {
            setActiveMessageId(m.messageId);
            setSelectionAnchorId(m.messageId);
            if (!m.isRead) markRead.mutate({ messageId: m.messageId, value: true });
          }}
          className={`min-w-0 flex-1 py-2.5 pr-3 text-left ${group.anyUnread ? 'font-semibold' : ''}`}
        >
          <div className="flex items-start justify-between gap-2">
            <span className="truncate text-sm">{m.fromName || m.fromAddress}</span>
            <span className="shrink-0 text-[11px] text-muted-foreground">
              {(() => {
                const d = m.receivedAt ?? m.sentAt ?? m.createdAt;
                return d ? formatListDate(d) : '';
              })()}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-sm">
            <span className="truncate">{group.subject || '(no subject)'}</span>
            <span className="shrink-0 rounded-full bg-muted px-1.5 text-[11px] tabular-nums text-muted-foreground">
              {group.count}
            </span>
          </div>
          <div
            className={`text-xs text-muted-foreground ${PREVIEW_LINE_CLAMP[listPrefs.previewLines]}`}
          >
            {m.snippet}
          </div>
        </button>
        {group.anyStarred && (
          <span className="flex shrink-0 items-center px-2 text-yellow-500">
            <Star className="h-4 w-4 fill-current" />
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="relative flex h-full min-h-0">
      {/* Mobile: tap-outside backdrop for the folder drawer. */}
      {isMobile && mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40"
          aria-hidden
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      {/* Sidebar: account + folders. On mobile it's an off-canvas drawer; on
          desktop its width is user-resizable (see the ColumnResizer below). */}
      <aside
        className={
          isMobile
            ? `${mobileSidebarOpen ? 'fixed inset-y-0 left-0 z-50 w-72 shadow-xl' : 'hidden'} flex flex-col border-r bg-background`
            : 'flex shrink-0 flex-col border-r'
        }
        style={isMobile ? undefined : { width: columnWidths.sidebar }}
      >
        <div className="p-2 border-b space-y-2">
          {/* Focus-mode restore controls — only while chrome is hidden. Kept here
              (above the account selector) so restoring doesn't push the message
              list down, unlike a full-width bar above the panes. */}
          {headersHidden && (
            <div className="flex items-center gap-1">
              <Link
                to="/"
                className="rounded-md border border-border bg-card/90 p-1.5 shadow-sm hover:opacity-80"
                title="Back to Fias home"
                aria-label="Back to Fias home"
              >
                <img src="/fias-logo.png" alt="Fias" className="h-4 w-auto dark:invert" />
              </Link>
              <button
                type="button"
                onClick={() => showHeaders('arche_email')}
                className="rounded-md border border-border bg-card/90 p-1.5 text-muted-foreground shadow-sm hover:text-primary"
                title="Show headers"
                aria-label="Show headers"
              >
                <PanelTop className="h-4 w-4" />
              </button>
            </div>
          )}
          <select
            aria-label="Account"
            className="w-full h-9 rounded-md border bg-background px-2 text-sm"
            value={isUnified ? UNIFIED_ACCOUNT : (accountId ?? '')}
            onChange={(e) => {
              setActiveAccountId(e.target.value);
              setActiveFolderId(null);
              setUnifiedFocus(null);
              setScreenerView(false);
              setActiveMessageId(null);
              setSenderFilter(null);
            }}
          >
            {accounts.length > 1 && <option value={UNIFIED_ACCOUNT}>All accounts</option>}
            {accounts.map((a) => (
              <option key={a.accountId} value={a.accountId}>
                {a.emailAddress}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            className="w-full gap-1"
            disabled={accounts.length === 0}
            onClick={() => {
              setComposeDraft(undefined);
              setComposeOpen(true);
              setMobileSidebarOpen(false);
            }}
          >
            <PenSquare className="h-4 w-4" />
            Compose
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-1"
            // Single-account: stream that account. Unified: stream every
            // connected account in sequence (needs at least one to sync).
            disabled={
              syncStream.syncing || (isUnified ? (accounts?.length ?? 0) === 0 : !accountId)
            }
            onClick={() => {
              if (syncStream.syncing) return;
              if (isUnified) {
                syncStream.startAll(
                  (accounts ?? []).map((a) => ({
                    accountId: a.accountId,
                    emailAddress: a.emailAddress,
                  })),
                );
              } else if (accountId) {
                syncStream.start(accountId);
              }
            }}
          >
            {syncStream.syncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Sync all
          </Button>
          {syncStream.syncing && (
            <div className="space-y-0.5">
              {syncStream.accountScope && (
                <p className="text-xs text-muted-foreground truncate">
                  {syncStream.accountScope.emailAddress} · account {syncStream.accountScope.index}{' '}
                  of {syncStream.accountScope.total}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {syncStream.progress ? formatSyncProgress(syncStream.progress) : 'Starting sync…'}
              </p>
            </div>
          )}
          {syncStream.error && <p className="text-xs text-destructive">{syncStream.error}</p>}
          {syncStream.failures.length > 1 && (
            <ul className="mt-0.5 space-y-0.5 text-xs text-destructive">
              {syncStream.failures.map((f) => (
                <li key={f.emailAddress}>
                  <span className="font-medium">{f.emailAddress}</span>: {f.message}
                </li>
              ))}
            </ul>
          )}
          {!syncStream.syncing && syncStream.result && (
            <p className="text-xs text-muted-foreground">
              Synced {syncStream.result.messagesUpserted} message(s)
            </p>
          )}
          {/* Background/auto-sync failure — only when the stream isn't already
              showing its own error, so we never double up. */}
          {!syncStream.syncing && !syncStream.error && autoSyncError && (
            <p className="text-xs text-destructive">
              Sync failed — {friendlySyncError(autoSyncError.message)}
            </p>
          )}
        </div>
        <nav
          className="flex-1 overflow-y-auto p-1"
          onClick={() => isMobile && setMobileSidebarOpen(false)}
        >
          {isUnified ? (
            // Unified view: the "All inboxes" button on top (back to the merged
            // list), then every account's folders grouped by account. Clicking a
            // folder FOCUSES its contents without leaving the merged view; the
            // sidebar stays so you can hop to another account's folder. Folders
            // are also drag-drop targets (no management menu — that's single-account).
            <>
              <button
                type="button"
                className={`w-full px-2 py-1.5 rounded text-sm flex items-center gap-2 font-medium ${
                  isMerged && !screenerView ? 'bg-accent' : 'hover:bg-accent/50'
                }`}
                onClick={showAllInboxes}
              >
                <Inbox className="h-3.5 w-3.5 text-muted-foreground" /> All inboxes
              </button>
              {/* All Screened: held mail across every account, just below All
                  Inboxes so a screener backlog is impossible to miss. */}
              {anyScreenerEnabled && (
                <button
                  type="button"
                  className={`w-full px-2 py-1.5 rounded text-sm flex items-center gap-2 font-medium ${
                    screenerView ? 'bg-accent' : 'hover:bg-accent/50'
                  }`}
                  onClick={openScreener}
                >
                  <ShieldAlert className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>All Screened</span>
                  {screenerCount > 0 && (
                    <span className="ml-auto shrink-0 rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums text-primary">
                      {screenerCount.toLocaleString()}
                    </span>
                  )}
                </button>
              )}
              {(accounts ?? []).map((acct) => {
                const acctFolders = sortFolders(foldersByAccount.get(acct.accountId) ?? []);
                const acctFolderTree = buildFolderTree(acctFolders, collapsedFolders.isCollapsed);
                const collapsed = collapsedAccounts.isCollapsed(acct.accountId);
                // Total unread across the account's folders — kept visible on the
                // collapsed header so minimizing an account doesn't hide whether it
                // still needs attention.
                const acctUnread = acctFolders.reduce((n, f) => n + (f.unreadCount ?? 0), 0);
                return (
                  <div key={acct.accountId} className="mt-2">
                    {/* Collapse/expand toggle: minimize accounts you aren't focused
                        on so a few stay in view. Persisted per-device. */}
                    <button
                      type="button"
                      aria-expanded={!collapsed}
                      onClick={() => collapsedAccounts.toggle(acct.accountId)}
                      className="flex w-full items-center gap-1 rounded px-2 pb-0.5 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:bg-accent/50"
                      title={
                        collapsed ? `Expand ${acct.emailAddress}` : `Collapse ${acct.emailAddress}`
                      }
                    >
                      {collapsed ? (
                        <ChevronRight className="h-3 w-3 shrink-0" />
                      ) : (
                        <ChevronDown className="h-3 w-3 shrink-0" />
                      )}
                      <span className="truncate">{acct.emailAddress}</span>
                      {collapsed && acctUnread > 0 && (
                        <span className="ml-auto shrink-0 tabular-nums font-semibold normal-case text-primary">
                          {acctUnread.toLocaleString()}
                        </span>
                      )}
                    </button>
                    {!collapsed &&
                      acctFolderTree.map(({ folder: f, depth, hasChildren }) => (
                        <FolderSidebarItem
                          key={f.folderId}
                          folder={f}
                          depth={depth}
                          hasChildren={hasChildren}
                          isExpanded={hasChildren && !collapsedFolders.isCollapsed(f.folderId)}
                          onToggleExpand={() => collapsedFolders.toggle(f.folderId)}
                          isActive={
                            unifiedFocus?.accountId === acct.accountId &&
                            unifiedFocus?.folderId === f.folderId
                          }
                          onSelect={() => focusUnifiedFolder(acct.accountId, f.folderId)}
                          canAcceptDrop={canDropOnFolder(f)}
                          onDropMessage={(opts) => handleDropOnFolder(f, opts)}
                        />
                      ))}
                  </div>
                );
              })}
            </>
          ) : (
            <>
              {/* Screener: this account's held mail, sitting above the folder list
                  (right by Inbox) with a count so a backlog is impossible to miss. */}
              {screenerEnabled && (
                <button
                  type="button"
                  className={`w-full px-2 py-1.5 rounded text-sm flex items-center gap-2 ${
                    screenerView ? 'bg-accent font-medium' : 'hover:bg-accent/50'
                  }`}
                  onClick={openScreener}
                >
                  <ShieldAlert className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Screener</span>
                  {screenerCount > 0 && (
                    <span className="ml-auto shrink-0 rounded-full bg-primary/15 px-1.5 text-[11px] font-semibold tabular-nums text-primary">
                      {screenerCount.toLocaleString()}
                    </span>
                  )}
                </button>
              )}
              {folderTree.map(({ folder: f, depth, hasChildren }) => {
                const isCustom = f.folderType === 'custom';
                const canEmpty = f.systemRole === 'trash' || f.systemRole === 'spam';
                return (
                  <FolderSidebarItem
                    key={f.folderId}
                    folder={f}
                    depth={depth}
                    hasChildren={hasChildren}
                    isExpanded={hasChildren && !collapsedFolders.isCollapsed(f.folderId)}
                    onToggleExpand={() => collapsedFolders.toggle(f.folderId)}
                    isActive={!screenerView && f.folderId === inboxFolderId}
                    alwaysShowMenu={isMobile}
                    onSelect={() => selectFolder(f.folderId)}
                    onMarkRead={() => markFolderRead(f.folderId, true)}
                    onMarkUnread={() => markFolderRead(f.folderId, false)}
                    onEmpty={
                      canEmpty
                        ? () =>
                            setConfirmEmptyFolder({
                              folderId: f.folderId,
                              name: f.name,
                              role: f.systemRole,
                            })
                        : undefined
                    }
                    onRename={
                      isCustom
                        ? () =>
                            setFolderNameDialog({
                              mode: 'rename',
                              folderId: f.folderId,
                              current: f.name,
                            })
                        : undefined
                    }
                    onDelete={
                      isCustom
                        ? () => setConfirmDeleteFolder({ folderId: f.folderId, name: f.name })
                        : undefined
                    }
                    onCreateChild={
                      // Nest under custom folders only (they have a server path);
                      // expand the parent so the new child is immediately visible.
                      isCustom
                        ? () => {
                            collapsedFolders.expand(f.folderId);
                            setFolderNameDialog({
                              mode: 'create',
                              parentFolderId: f.folderId,
                              parentName: f.name,
                            });
                          }
                        : undefined
                    }
                    canAcceptDrop={canDropOnFolder(f)}
                    onDropMessage={(opts) => handleDropOnFolder(f, opts)}
                  />
                );
              })}
              <button
                className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                onClick={() => setFolderNameDialog({ mode: 'create' })}
              >
                <FolderPlus className="h-3.5 w-3.5" /> New folder
              </button>
            </>
          )}
          {/* Shared Tools — rendered in BOTH single-account and unified views,
              collapsible to keep the sidebar tidy. In unified mode the per-account
              dialogs below scope to one account via their own picker. */}
          <div className="mt-2">
            <button
              type="button"
              aria-expanded={!toolsCollapsed}
              onClick={toggleToolsCollapsed}
              className="flex w-full items-center gap-1 rounded px-2 pb-0.5 pt-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:bg-accent/50"
            >
              {toolsCollapsed ? (
                <ChevronRight className="h-3 w-3 shrink-0" />
              ) : (
                <ChevronDown className="h-3 w-3 shrink-0" />
              )}
              <span>Tools</span>
            </button>
            {!toolsCollapsed && (
              <>
                {toolsScheduleEnabled && (
                  <button
                    className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                    onClick={() => setScheduledOpen(true)}
                  >
                    <Clock className="h-3.5 w-3.5" /> Scheduled
                  </button>
                )}
                {toolsSnoozeEnabled && (
                  <button
                    className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                    onClick={() => setSnoozedListOpen(true)}
                  >
                    <AlarmClock className="h-3.5 w-3.5" /> Snoozed
                  </button>
                )}
                <button
                  className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                  onClick={() => setContactsOpen(true)}
                >
                  <Users className="h-3.5 w-3.5" /> Contacts
                </button>
                <button
                  className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                  onClick={() => setFiltersOpen(true)}
                >
                  <Filter className="h-3.5 w-3.5" /> Filters
                </button>
                <button
                  className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                  onClick={() => setScreenerOpen(true)}
                >
                  <Inbox className="h-3.5 w-3.5" /> Screener
                </button>
                <button
                  className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                  onClick={() => setSpamOpen(true)}
                >
                  <ShieldAlert className="h-3.5 w-3.5" /> Spam &amp; AI Filter
                </button>
                <button
                  className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                  onClick={() => setVacationOpen(true)}
                >
                  <Plane className="h-3.5 w-3.5" /> Vacation responder
                </button>
                <button
                  className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                  onClick={() => setSettingsOpen(true)}
                >
                  <Settings className="h-3.5 w-3.5" /> Settings
                </button>
                <Link
                  to="../connections"
                  className="w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 hover:bg-accent/50 text-muted-foreground"
                >
                  <UserCog className="h-3.5 w-3.5" /> Accounts
                </Link>
              </>
            )}
          </div>
        </nav>
      </aside>

      {/* Drag handle: resize the accounts sidebar (desktop only). */}
      {!isMobile && (
        <ColumnResizer
          width={columnWidths.sidebar}
          min={EMAIL_COLUMN_BOUNDS.sidebar.min}
          max={EMAIL_COLUMN_BOUNDS.sidebar.max}
          onResize={(w) => setColumnWidths({ sidebar: w })}
          ariaLabel="Resize accounts column"
        />
      )}

      {/* Message list. On mobile it's the full-width view, hidden once a message
          opens (the reading pane takes over). On desktop its width is
          user-resizable (see the ColumnResizer below). */}
      <div
        className={
          isMobile
            ? `${activeMessageId || anyGrouping ? 'hidden' : 'flex'} w-full min-h-0 flex-col`
            : 'flex shrink-0 flex-col border-r min-h-0'
        }
        style={isMobile ? undefined : { width: columnWidths.list }}
      >
        {isMobile && (
          <div className="flex items-center gap-2 border-b p-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              aria-label="Folders"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
            <span className="truncate text-sm font-medium">
              {screenerView
                ? isUnified
                  ? 'All Screened'
                  : 'Screener'
                : isMerged
                  ? 'All inboxes'
                  : (activeFolder?.name ?? 'Mail')}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-8 w-8 p-0"
              aria-label="Compose"
              onClick={() => {
                setComposeDraft(undefined);
                setComposeOpen(true);
              }}
            >
              <PenSquare className="h-4 w-4" />
            </Button>
          </div>
        )}
        {!isUnified && activeFolder && (
          <div className="px-3 pt-2 flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-medium">{viewHeading}</span>
            <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
              {listCountLabel}
            </span>
          </div>
        )}
        {isUnified && (
          <div className="px-3 pt-2 flex items-baseline justify-between gap-2">
            <span className="truncate text-sm font-medium">{viewHeading}</span>
            <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
              {listCountLabel}
            </span>
          </div>
        )}
        {viewingTrash && (activeFolder?.messageCount ?? 0) > 0 && (
          <div className="px-3 pt-1 text-[11px]">
            {confirmEmptyTrash ? (
              <span className="flex items-center gap-2">
                <span className="text-muted-foreground">Permanently delete all trash?</span>
                <button
                  className="font-medium text-destructive hover:underline"
                  onClick={() => {
                    setConfirmEmptyTrash(false);
                    if (contentAccountId) emptyTrash.mutate(contentAccountId);
                  }}
                >
                  Delete forever
                </button>
                <button
                  className="text-muted-foreground hover:underline"
                  onClick={() => setConfirmEmptyTrash(false)}
                >
                  Cancel
                </button>
              </span>
            ) : (
              <button
                className="inline-flex items-center gap-1 text-destructive hover:underline disabled:opacity-50"
                disabled={emptyTrash.isPending}
                onClick={() => setConfirmEmptyTrash(true)}
              >
                {emptyTrash.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
                Empty Trash
              </button>
            )}
          </div>
        )}
        <div className="p-2 border-b space-y-1.5">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="search"
              aria-label={searchMode === 'search' ? 'Search all mail' : 'Filter this view'}
              placeholder={
                searchMode === 'search' ? 'Search all accounts & folders…' : 'Filter this view…'
              }
              className="w-full h-8 rounded-md border bg-background pl-2 pr-7 text-sm"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setActiveMessageId(null);
                if (e.target.value.trim()) {
                  setGroupMode('none');
                  setSenderFilter(null);
                  setCategoryFilter(undefined);
                }
              }}
            />
            {searchTerm && (
              <button
                type="button"
                aria-label="Clear search"
                className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                onClick={() => {
                  setSearchTerm('');
                  setActiveMessageId(null);
                  searchInputRef.current?.focus();
                }}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {/* Quick filters — available in both the folder view AND while searching. */}
          <div className="flex flex-wrap items-center gap-1">
            <FilterChip
              active={filterUnread}
              onClick={() => setFilterUnread((v) => !v)}
              icon={Mail}
              label="Unread"
            />
            <FilterChip
              active={filterStarred}
              onClick={() => setFilterStarred((v) => !v)}
              icon={Star}
              label="Starred"
            />
            <FilterChip
              active={filterAttachments}
              onClick={() => setFilterAttachments((v) => !v)}
              icon={Paperclip}
              label="Attachments"
            />
            <span className="mx-0.5 h-3.5 w-px bg-border" />
            <FilterChip
              active={bySender}
              onClick={() => toggleGroupMode('sender')}
              icon={Users}
              label="By Sender"
            />
            <FilterChip
              active={byCategory}
              onClick={() => toggleGroupMode('category')}
              icon={Tag}
              label="By Category"
            />
          </div>
          {/* Active sender drill-down (from the By Sender overview): a dismissible
              pill showing whose mail the list is narrowed to, plus the shown count. */}
          {senderFilter && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-muted-foreground">From:</span>
              <span className="inline-flex min-w-0 items-center gap-1 rounded-full border border-primary bg-primary/10 px-2 py-0.5">
                <span className="max-w-[180px] truncate font-medium" title={senderFilter}>
                  {senderFilter}
                </span>
                <button
                  type="button"
                  aria-label="Clear sender filter"
                  className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  onClick={() => setSenderFilter(null)}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {shownCount.toLocaleString()} shown
              </span>
            </div>
          )}
          {/* Active category drill-down (from the By Category overview): a
              dismissible pill showing which category the list is narrowed to. */}
          {categoryFilter !== undefined && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-muted-foreground">Category:</span>
              <span className="inline-flex min-w-0 items-center gap-1 rounded-full border border-primary bg-primary/10 px-2 py-0.5">
                <span
                  className="max-w-[180px] truncate font-medium"
                  title={categoryFilterName ?? ''}
                >
                  {categoryFilterName}
                </span>
                <button
                  type="button"
                  aria-label="Clear category filter"
                  className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  onClick={clearCategoryFilter}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {shownCount.toLocaleString()} shown
              </span>
            </div>
          )}
          {/* Active label drill-down (clicked a label chip): a dismissible pill
              showing which label the list is narrowed to. */}
          {labelFilter !== undefined && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-muted-foreground">Label:</span>
              <span className="inline-flex min-w-0 items-center gap-1 rounded-full border border-primary bg-primary/10 px-2 py-0.5">
                <span className="max-w-[180px] truncate font-medium" title={labelFilterName ?? ''}>
                  {labelFilterName}
                </span>
                <button
                  type="button"
                  aria-label="Clear label filter"
                  className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  onClick={clearLabelFilter}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {shownCount.toLocaleString()} shown
              </span>
            </div>
          )}
          {/* Mode toggle: does the box FILTER the current view (instant, in place)
              or SEARCH globally across all accounts + folders? Shown once there's a
              query; the choice is persisted. */}
          {hasQuery && (
            <div className="flex flex-wrap items-center gap-1 text-[11px]">
              <span className="text-muted-foreground">Mode:</span>
              <FilterChip
                active={searchMode === 'filter'}
                onClick={() => setSearchMode('filter')}
                icon={Filter}
                label="Filter"
              />
              <FilterChip
                active={searchMode === 'search'}
                onClick={() => setSearchMode('search')}
                icon={Search}
                label="Search"
              />
            </div>
          )}
          {/* Combinable field toggles — only meaningful for a global search
              (none selected = search all fields). */}
          {searchActive && (
            <div className="flex flex-wrap items-center gap-1 text-[11px]">
              <span className="text-muted-foreground">In:</span>
              {SEARCH_FIELDS.map((f) => (
                <FilterChip
                  key={f.value}
                  active={searchFields.has(f.value)}
                  onClick={() => toggleSearchField(f.value)}
                  label={f.label}
                />
              ))}
            </div>
          )}
          {searchActive ? (
            <p className="text-[11px] text-muted-foreground">
              Searching all accounts &amp; folders (synced mail)
            </p>
          ) : filterActive ? (
            <p className="text-[11px] text-muted-foreground">Filtering this view in place</p>
          ) : (
            !isUnified &&
            syncFolder.isPending && (
              <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Syncing…
              </p>
            )
          )}
        </div>
        {/* Toolbar: selection-mode toggle (left) + undo/redo (right). */}
        <div className="flex items-center gap-1 border-b px-2 py-1">
          <Button
            variant={selectionMode ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            aria-pressed={selectionMode}
            title="Select messages"
            onClick={toggleSelectionMode}
          >
            <CheckSquare className="h-3.5 w-3.5" /> Select
          </Button>
          <span className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            disabled={!history.canUndo}
            title={history.undoLabel ? `Undo: ${history.undoLabel}` : 'Nothing to undo'}
            aria-label="Undo"
            onClick={history.undo}
          >
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0"
            disabled={!history.canRedo}
            title={history.redoLabel ? `Redo: ${history.redoLabel}` : 'Nothing to redo'}
            aria-label="Redo"
            onClick={history.redo}
          >
            <Redo2 className="h-4 w-4" />
          </Button>
        </div>
        {selectionMode && (
          <div className="space-y-1.5 border-b bg-accent/40 px-2 py-1.5">
            {/* Line 1: count · select-all · exit. */}
            <div className="flex items-center gap-2 text-xs">
              <span className="font-medium">
                {selectAllInFolder
                  ? `All${wholeFolderCountLabel} selected`
                  : `${selectedCount} selected`}
              </span>
              <button
                type="button"
                className="text-[11px] text-primary hover:underline"
                onClick={allShownSelected || selectAllInFolder ? clearSelection : selectAllShown}
              >
                {allShownSelected || selectAllInFolder
                  ? 'Deselect all'
                  : `Select all${messages?.length ? ` ${messages.length}` : ''}`}
              </button>
              {/* "Select all N in folder": escalate from the loaded window to the
                  whole folder, so a bulk action hits everything — not just 50. */}
              {allShownSelected && !selectAllInFolder && moreInFolderThanLoaded && (
                <button
                  type="button"
                  className="text-[11px] font-medium text-primary hover:underline"
                  onClick={() => {
                    selectAllShown();
                    setSelectAllInFolder(true);
                  }}
                >
                  {isUnified
                    ? 'Select all in all inboxes'
                    : `Select all${wholeFolderCountLabel} in folder`}
                </button>
              )}
              <span className="flex-1" />
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                title="Exit selection"
                aria-label="Exit selection"
                onClick={() => {
                  clearSelection();
                  setSelectionMode(false);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {/* Line 2: bulk actions. */}
            <div className="flex flex-wrap items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={selectedCount === 0}
                title="Mark read"
                aria-label="Mark selected read"
                onClick={() =>
                  selectAllInFolder
                    ? runWholeFolderFlag('read', true)
                    : runBulk((msgs) => actMarkRead(msgs, true), { keepSelection: true })
                }
              >
                <MailOpen className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={selectedCount === 0}
                title="Mark unread"
                aria-label="Mark selected unread"
                onClick={() =>
                  selectAllInFolder
                    ? runWholeFolderFlag('read', false)
                    : runBulk((msgs) => actMarkRead(msgs, false), { keepSelection: true })
                }
              >
                <Mail className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={selectedCount === 0}
                title="Star"
                aria-label="Star selected"
                onClick={() =>
                  selectAllInFolder
                    ? runWholeFolderFlag('star', true)
                    : runBulk((msgs) => actStar(msgs, true), { keepSelection: true })
                }
              >
                <Star className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                disabled={selectedCount === 0}
                title="Archive"
                aria-label="Archive selected"
                onClick={() =>
                  selectAllInFolder
                    ? setConfirmWholeFolder({ kind: 'archive' })
                    : runBulk((msgs) => actArchive(msgs), { closesOpen: true })
                }
              >
                <Archive className="h-4 w-4" />
              </Button>
              {!searchActive && (
                <select
                  className="h-7 rounded-md border bg-background px-1 text-xs disabled:opacity-50"
                  aria-label="Move selected to folder"
                  disabled={selectedCount === 0}
                  value=""
                  onChange={(e) => {
                    const key = e.target.value;
                    if (!key) return;
                    // Whole-folder move is a single-account, folder-id action; a
                    // unified role move applies to the loaded selection only.
                    if (!isUnified && selectAllInFolder && !key.startsWith('role:')) {
                      const dest = sortedFolders.find((f) => f.folderId === key);
                      setConfirmWholeFolder({
                        kind: 'move',
                        targetFolderId: key,
                        folderName: dest?.name ?? 'folder',
                      });
                    } else {
                      runBulk((msgs) => moveMsgsTo(msgs, key), { closesOpen: true });
                    }
                  }}
                >
                  <option value="">Move…</option>
                  {moveDestinations.map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}
                    </option>
                  ))}
                </select>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive"
                disabled={selectedCount === 0}
                title="Delete"
                aria-label="Delete selected"
                onClick={() => {
                  if (selectAllInFolder) setConfirmWholeFolder({ kind: 'trash' });
                  else if (selectedCount > 0) setConfirmBulkDelete(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        <div className="flex-1 min-h-0">
          {messagesLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !messages || messages.length === 0 ? (
            <div className="text-center py-12 text-sm text-muted-foreground">
              {screenerView
                ? 'Your screener is empty — no first-time senders waiting.'
                : senderFilter
                  ? 'No messages from this sender in the loaded view.'
                  : categoryFilter !== undefined
                    ? 'No messages in this category here.'
                    : filterActive
                      ? 'No messages in this view match your filter.'
                      : searchActive
                        ? 'No matches in synced mail.'
                        : 'No messages in this folder yet.'}
            </div>
          ) : (
            <Virtuoso
              style={VIRTUOSO_STYLE}
              data={displayRows}
              computeItemKey={computeDisplayRowKey}
              increaseViewportBy={VIRTUOSO_VIEWPORT}
              itemContent={(_index, row) => {
                if (row.kind === 'thread') return renderThreadHeader(row.group);
                const m = row.message;
                const indented = row.indented ?? false;
                const selected = selectedIds.has(m.messageId);
                return (
                  <div
                    data-testid={`mail-row-${m.messageId}`}
                    // Desktop drag-to-folder. Disabled on touch (that gesture is
                    // swipe-to-act) and while picking rows for a bulk action.
                    draggable={!isMobile && !selectionMode}
                    onDragStart={
                      !isMobile && !selectionMode
                        ? (e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', m.messageId);
                            setDraggingMessage({
                              messageId: m.messageId,
                              accountId: m.accountId,
                              folderId: m.folderId,
                            });
                          }
                        : undefined
                    }
                    onDragEnd={
                      !isMobile && !selectionMode ? () => setDraggingMessage(null) : undefined
                    }
                    className={`group/mailrow flex items-stretch border-b ${
                      indented ? 'border-l-2 border-l-primary/30 pl-3' : ''
                    } ${
                      selected
                        ? 'bg-accent/40'
                        : m.messageId === activeMessageId
                          ? 'bg-accent'
                          : 'hover:bg-accent/40'
                    }`}
                    onTouchStart={
                      isMobile && swipePrefs.enabled
                        ? (e) => {
                            const t = e.changedTouches[0];
                            touchStartRef.current = { x: t.clientX, y: t.clientY };
                            swipedRef.current = false;
                          }
                        : undefined
                    }
                    onTouchEnd={
                      isMobile && swipePrefs.enabled
                        ? (e) => {
                            const start = touchStartRef.current;
                            touchStartRef.current = null;
                            if (!start) return;
                            const t = e.changedTouches[0];
                            const dx = t.clientX - start.x;
                            const dy = t.clientY - start.y;
                            // Horizontal intent only, past a comfortable threshold.
                            if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy)) return;
                            performSwipe(m, dx < 0 ? swipePrefs.left : swipePrefs.right);
                          }
                        : undefined
                    }
                  >
                    {selectionMode && (
                      <label
                        className="flex items-center pl-2 pr-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5"
                          aria-label={`Select ${m.subject || '(no subject)'}`}
                          checked={selected}
                          readOnly
                          // Shift-click selects a range, so the toggle has to read
                          // the modifier key — onChange (a plain Event) doesn't
                          // carry it, onClick (a MouseEvent) does.
                          onMouseDown={(e) => {
                            // Stop Shift-click from painting a text selection over the list.
                            if (e.shiftKey) e.preventDefault();
                          }}
                          onClick={(e) => handleSelectClick(m.messageId, e.shiftKey)}
                        />
                      </label>
                    )}
                    <button
                      // Shift-click the body to select a range, so suppress the
                      // browser's text-highlight that a Shift-click would otherwise
                      // paint across the rows.
                      onMouseDown={(e) => {
                        if (e.shiftKey) e.preventDefault();
                      }}
                      onClick={(e) => {
                        // A swipe synthesizes a click — ignore it so a swipe-to-act
                        // gesture doesn't also open the message.
                        if (swipedRef.current) {
                          swipedRef.current = false;
                          return;
                        }
                        // Shift-click the body extends the selection (entering
                        // selection mode if needed) rather than opening the message —
                        // click one row, Shift-click another, and the whole run between
                        // them is selected. The anchor is whatever row was last opened
                        // or selected.
                        if (e.shiftKey) {
                          setSelectionMode(true);
                          selectRangeToAnchor(m.messageId);
                          return;
                        }
                        // Opening a message from a grouping overview leaves the
                        // overview so the reading pane (or composer) can show it.
                        if (anyGrouping) setGroupMode('none');
                        // A Drafts message reopens in the composer to continue editing,
                        // rather than the read-only pane.
                        if (activeFolderRole === 'drafts') {
                          void openDraftForEditing(m.messageId);
                          return;
                        }
                        setActiveMessageId(m.messageId);
                        // Anchor a later Shift-click on the row the user just opened.
                        setSelectionAnchorId(m.messageId);
                        summarize.reset();
                        categorize.reset();
                        // Opening an unread message marks it read (optimistic).
                        if (!m.isRead) markRead.mutate({ messageId: m.messageId, value: true });
                      }}
                      className={`min-w-0 flex-1 py-2.5 pr-3 text-left ${
                        selectionMode ? '' : 'pl-3'
                      } ${m.isRead ? '' : 'font-semibold'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex min-w-0 items-center gap-1">
                          <span className="truncate text-sm">
                            {showRecipients
                              ? `To: ${
                                  m.toAddresses.map((a) => a.name || a.email).join(', ') ||
                                  '(no recipient)'
                                }`
                              : m.fromName || m.fromAddress}
                          </span>
                          {/* Verified-sender mark — only in the "from" view (not Sent). */}
                          {!showRecipients && <VerifiedBadge auth={m.spamAuthSummary} />}
                        </span>
                        <span className="flex shrink-0 flex-col items-end text-[11px] text-muted-foreground">
                          <span>
                            {(() => {
                              const d = m.receivedAt ?? m.sentAt ?? m.createdAt;
                              return d ? formatListDate(d) : '';
                            })()}
                          </span>
                          {formatBytes(m.sizeBytes) && (
                            <span className="tabular-nums">{formatBytes(m.sizeBytes)}</span>
                          )}
                        </span>
                      </div>
                      {isUnified && (
                        <div className="truncate text-[11px] text-muted-foreground">
                          {accountAddressById.get(m.accountId) ?? m.accountId}
                        </div>
                      )}
                      <div className="truncate text-sm">{m.subject || '(no subject)'}</div>
                      {/* Label chips (display only — the row itself is a button, so
                          the clickable drill-down lives in the reading pane). */}
                      {m.labels && m.labels.length > 0 && (
                        <LabelChips labels={m.labels} className="mt-0.5" />
                      )}
                      <div className="flex items-start gap-1 text-xs text-muted-foreground">
                        {/* Star now lives in the trailing toggle; the paperclip stays
                            as an inline indicator. */}
                        {m.hasAttachments && (
                          <span className="flex shrink-0 items-center pt-px">
                            <Paperclip className="h-3 w-3" />
                          </span>
                        )}
                        <span className={`min-w-0 ${PREVIEW_LINE_CLAMP[listPrefs.previewLines]}`}>
                          {m.snippet}
                        </span>
                      </div>
                    </button>
                    {/* Drafts open in the composer (not the reader), so its Delete
                        button never applies — give draft rows their own hover-reveal
                        trash so a draft can be discarded without opening it. */}
                    {activeFolderRole === 'drafts' && (
                      <button
                        type="button"
                        aria-label="Delete draft"
                        title="Delete draft"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          if (e.shiftKey) e.preventDefault();
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Drafts get a dedicated hard-discard (local + server),
                          // not a move-to-Trash — so it leaves Drafts for good.
                          discardDraft.mutate(m.messageId);
                        }}
                        className={`flex shrink-0 items-start px-2 py-2.5 text-muted-foreground transition-opacity hover:text-destructive ${
                          isMobile
                            ? 'opacity-50'
                            : 'opacity-0 focus:opacity-100 group-hover/mailrow:opacity-100'
                        }`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    {/* Screener rows: inline Approve (let the sender into the inbox)
                        / Reject (trash their mail). Always visible here — the whole
                        point of the view is to decide these, so don't hide them
                        behind hover. Decides by sender ADDRESS (the row has it). */}
                    {screenerView && (
                      <span className="flex shrink-0 items-start gap-0.5 pr-1">
                        <button
                          type="button"
                          aria-label={`Approve sender ${m.fromAddress}`}
                          title="Approve sender — let their mail into your inbox"
                          disabled={decideSender.isPending}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            if (e.shiftKey) e.preventDefault();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            decideSender.mutate({
                              accountId: m.accountId,
                              senderAddress: m.fromAddress,
                              decision: 'approved',
                            });
                          }}
                          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-emerald-500/10 hover:text-emerald-600 disabled:opacity-40"
                        >
                          <UserCheck className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Reject sender ${m.fromAddress}`}
                          title="Reject sender — move their mail to Trash"
                          disabled={decideSender.isPending}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            if (e.shiftKey) e.preventDefault();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            decideSender.mutate({
                              accountId: m.accountId,
                              senderAddress: m.fromAddress,
                              decision: 'rejected',
                            });
                          }}
                          className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                        >
                          <Ban className="h-4 w-4" />
                        </button>
                      </span>
                    )}
                    {/* Star toggle — star/unstar straight from the preview without
                        opening the message. Starred rows always show it; unstarred
                        reveal it on row hover (desktop) or keep it faint (touch), so
                        it stays discoverable without cluttering the list. */}
                    <button
                      type="button"
                      aria-label={m.isStarred ? 'Unstar message' : 'Star message'}
                      aria-pressed={m.isStarred}
                      title={m.isStarred ? 'Unstar' : 'Star'}
                      onMouseDown={(e) => {
                        // Keep a click here from painting a text selection or
                        // extending a Shift-range over the list.
                        e.stopPropagation();
                        if (e.shiftKey) e.preventDefault();
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        actStar([toLite(m)], !m.isStarred);
                      }}
                      className={`flex shrink-0 items-start px-2 py-2.5 transition-opacity ${
                        m.isStarred
                          ? 'text-yellow-500'
                          : `text-muted-foreground hover:text-yellow-500 ${
                              isMobile
                                ? 'opacity-50'
                                : 'opacity-0 focus:opacity-100 group-hover/mailrow:opacity-100'
                            }`
                      }`}
                    >
                      <Star className={`h-4 w-4 ${m.isStarred ? 'fill-current' : ''}`} />
                    </button>
                  </div>
                );
              }}
            />
          )}
        </div>
        {loadControlsBar}
      </div>

      {/* Drag handle: resize the message-list column (desktop only). */}
      {!isMobile && (
        <ColumnResizer
          width={columnWidths.list}
          min={EMAIL_COLUMN_BOUNDS.list.min}
          max={EMAIL_COLUMN_BOUNDS.list.max}
          onResize={(w) => setColumnWidths({ list: w })}
          ariaLabel="Resize message list column"
        />
      )}

      {/* Reading pane. On mobile it's full-width and only shown once a message
          is open, with a Back button that returns to the list. */}
      <div
        className={
          isMobile
            ? `${activeMessageId || anyGrouping ? 'flex' : 'hidden'} w-full min-h-0 flex-col ${anyGrouping ? '' : 'overflow-y-auto'}`
            : `flex-1 min-w-0 ${anyGrouping ? 'flex min-h-0 flex-col' : 'overflow-y-auto'}`
        }
      >
        {isMobile && (openMessage || anyGrouping) && (
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background p-2">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1"
              onClick={() => (anyGrouping ? setGroupMode('none') : setActiveMessageId(null))}
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </div>
        )}
        {bySender ? (
          <SenderGroupsView
            groups={senderGroups}
            loading={senderGroupsLoading}
            excludeStarred={bySenderExcludeStarred}
            excludeUnread={bySenderExcludeUnread}
            excludeAttachments={bySenderExcludeAttachments}
            onToggleExcludeStarred={() => setBySenderExcludeStarred((v) => !v)}
            onToggleExcludeUnread={() => setBySenderExcludeUnread((v) => !v)}
            onToggleExcludeAttachments={() => setBySenderExcludeAttachments((v) => !v)}
            onOpenSender={(g) => openSender(g.senderAddress)}
            onArchive={(g) => actArchive(g.messages)}
            onDelete={(g) => actTrash(g.messages)}
            onMove={(g, fid) =>
              actMove(g.messages, fid, `Moved ${g.count} from ${g.senderName || g.senderAddress}`)
            }
            folders={sortedFolders}
            canMove={!isUnified}
          />
        ) : byCategory ? (
          <CategoryGroupsView
            groups={categoryGroups}
            loading={categoryGroupsLoading}
            excludeStarred={bySenderExcludeStarred}
            excludeUnread={bySenderExcludeUnread}
            excludeAttachments={bySenderExcludeAttachments}
            onToggleExcludeStarred={() => setBySenderExcludeStarred((v) => !v)}
            onToggleExcludeUnread={() => setBySenderExcludeUnread((v) => !v)}
            onToggleExcludeAttachments={() => setBySenderExcludeAttachments((v) => !v)}
            onOpenCategory={(g: EmailCategoryGroupDto) => openCategory(g.categoryId)}
            onArchive={(g: EmailCategoryGroupDto) => actArchive(g.messages)}
            onDelete={(g: EmailCategoryGroupDto) => actTrash(g.messages)}
            onMove={(g: EmailCategoryGroupDto, fid: string) =>
              actMove(g.messages, fid, `Moved ${g.count} in ${g.name}`)
            }
            onManage={() => setCategoryManagerOpen(true)}
            onBulkCategorize={() => setBulkCategorizeOpen(true)}
            folders={sortedFolders}
            canMove={!isUnified}
          />
        ) : !openMessage ? (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            Select a message to read
          </div>
        ) : (
          <article className="p-6 max-w-3xl">
            {/* Action toolbar — optimistic; server writeback applies on next sync. */}
            <div className="flex items-center gap-1 mb-4 border-b pb-2">
              {/* Screener: decide the sender right from the open held message
                  (esp. on mobile, where the list's inline buttons aren't visible). */}
              {screenerView && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    title="Approve sender — let their mail into your inbox"
                    disabled={decideSender.isPending}
                    onClick={() => {
                      decideSender.mutate({
                        accountId: openMessage.accountId,
                        senderAddress: openMessage.fromAddress,
                        decision: 'approved',
                      });
                      setActiveMessageId(null);
                    }}
                  >
                    <UserCheck className="h-4 w-4" /> Approve
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-destructive hover:text-destructive"
                    title="Reject sender — move their mail to Trash"
                    disabled={decideSender.isPending}
                    onClick={() => {
                      decideSender.mutate({
                        accountId: openMessage.accountId,
                        senderAddress: openMessage.fromAddress,
                        decision: 'rejected',
                      });
                      setActiveMessageId(null);
                    }}
                  >
                    <Ban className="h-4 w-4" /> Reject
                  </Button>
                  <div className="mx-1 h-4 w-px bg-border" />
                </>
              )}
              <Button
                variant="ghost"
                size="sm"
                title="Reply (r)"
                aria-label="Reply"
                onClick={() => openCompose(buildReplyDraft(openMessage, false))}
              >
                <Reply className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title="Reply all (Shift+R)"
                aria-label="Reply all"
                onClick={() => openCompose(buildReplyDraft(openMessage, true))}
              >
                <ReplyAll className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title="Forward (f)"
                aria-label="Forward"
                onClick={() => openCompose(buildForwardDraft(openMessage, openAttachments))}
              >
                <Forward className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title={openMessage.isStarred ? 'Unstar (s)' : 'Star (s)'}
                aria-label={openMessage.isStarred ? 'Unstar' : 'Star'}
                aria-pressed={openMessage.isStarred}
                onClick={() => actStar([toLite(openMessage)], !openMessage.isStarred)}
              >
                <Star
                  className={`h-4 w-4 ${openMessage.isStarred ? 'fill-current text-yellow-500' : ''}`}
                />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title="Mark as unread (u)"
                aria-label="Mark as unread"
                onClick={() => {
                  actMarkRead([toLite(openMessage)], false);
                  setActiveMessageId(null);
                }}
              >
                <Mail className="h-4 w-4" />
              </Button>
              {userSettings.features.snoozeEnabled && (
                <Button
                  variant="ghost"
                  size="sm"
                  title="Snooze"
                  aria-label="Snooze"
                  onClick={() => setSnoozeOpen(true)}
                >
                  <AlarmClock className="h-4 w-4" />
                </Button>
              )}
              {archiveFolderId && openMessage.folderId !== archiveFolderId && (
                <Button
                  variant="ghost"
                  size="sm"
                  title="Archive (a)"
                  aria-label="Archive"
                  onClick={() => {
                    actArchive([toLite(openMessage)]);
                    setActiveMessageId(null);
                  }}
                >
                  <Archive className="h-4 w-4" />
                </Button>
              )}
              {spamFolderId && openMessage.folderId === spamFolderId ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Not spam"
                    aria-label="Not spam"
                    disabled={notSpam.isPending}
                    onClick={() => actNotSpam(openMessage.accountId, openMessage.messageId)}
                  >
                    <ShieldCheck className="h-4 w-4" />
                  </Button>
                  {/* Escalate an AI-detected (or any) Spam item to a hard user block:
                      moves it to Trash and adds the sender to "You blocked". */}
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Block sender & move to Trash"
                    aria-label="Block sender and move to Trash"
                    className="text-destructive"
                    disabled={reportSpam.isPending}
                    onClick={() => {
                      actReportSpam(openMessage.accountId, openMessage.messageId);
                      setActiveMessageId(null);
                    }}
                  >
                    <ShieldX className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  title="Report spam"
                  aria-label="Report spam"
                  disabled={reportSpam.isPending}
                  onClick={() => actReportSpam(openMessage.accountId, openMessage.messageId)}
                >
                  <ShieldAlert className="h-4 w-4" />
                </Button>
              )}
              <select
                className="h-8 rounded-md border bg-background px-2 text-sm"
                aria-label="Move to folder"
                value=""
                onChange={(e) => {
                  const key = e.target.value;
                  if (!key) return;
                  moveMsgsTo([toLite(openMessage)], key);
                  setActiveMessageId(null);
                }}
              >
                <option value="">Move to…</option>
                {moveDestinations
                  // Single-account: hide the folder the message is already in.
                  // Unified role targets have no folder id to compare against.
                  .filter((d) => isUnified || d.key !== openMessage.folderId)
                  .map((d) => (
                    <option key={d.key} value={d.key}>
                      {d.label}
                    </option>
                  ))}
              </select>
              <Button
                variant="ghost"
                size="sm"
                title="Delete (Del)"
                aria-label="Delete"
                onClick={() => {
                  actTrash([toLite(openMessage)]);
                  setActiveMessageId(null);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <span className="mx-1 h-4 w-px bg-border" />
              <Button
                variant="ghost"
                size="sm"
                title="Summarize (AI)"
                aria-label="Summarize"
                disabled={summarize.isPending}
                onClick={() => summarize.mutate(openMessage.messageId)}
              >
                {summarize.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                title="Categorize (AI)"
                aria-label="Categorize"
                disabled={categorize.isPending}
                onClick={() => categorize.mutate(openMessage.messageId)}
              >
                {categorize.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Tag className="h-4 w-4" />
                )}
              </Button>
            </div>
            {spamFolderId && openMessage.folderId === spamFolderId && openMessage.spamReason && (
              <div className="mb-4 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <span>
                  <span className="font-medium">Flagged as spam:</span> {openMessage.spamReason}
                </span>
              </div>
            )}
            {summarize.data?.summary && (
              <div className="mb-4 rounded-md border bg-accent/40 p-3 text-sm">
                <div className="mb-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  <Sparkles className="h-3 w-3" /> AI summary
                </div>
                {summarize.data.summary}
              </div>
            )}
            {categorize.data?.category && (
              <p className="mb-2 text-xs text-muted-foreground">
                Categorized as <span className="font-medium">{categorize.data.category}</span>
              </p>
            )}
            {(summarize.isError || categorize.isError) && (
              <p className="mb-2 text-xs text-destructive">
                {((summarize.error ?? categorize.error) as Error)?.message}
              </p>
            )}
            <h1 className="text-lg font-semibold mb-1">{openMessage.subject || '(no subject)'}</h1>
            <div className="text-sm text-muted-foreground mb-4 space-y-0.5">
              <div>
                <span className="font-medium text-foreground">From:</span>{' '}
                <span className="inline-flex items-center gap-1 align-middle font-medium text-foreground">
                  {openMessage.fromName || openMessage.fromAddress}
                  <VerifiedBadge auth={openMessage.spamAuthSummary} />
                </span>{' '}
                &lt;{openMessage.fromAddress}&gt;
                {senderContact.isLoading ? null : senderContact.data ? (
                  <span className="ml-2 inline-flex items-center gap-1 align-middle text-xs text-muted-foreground">
                    <UserCheck className="h-3 w-3" /> In Contacts
                    <Link to="/a/arche_contact_book" className="underline hover:text-foreground">
                      View
                    </Link>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="ml-2 inline-flex items-center gap-1 align-middle text-xs text-muted-foreground hover:text-foreground"
                    title="Add sender to contacts"
                    onClick={() => setAddContactOpen(true)}
                  >
                    <UserPlus className="h-3 w-3" /> Add to contacts
                  </button>
                )}
              </div>
              <div>
                <span className="font-medium text-foreground">To:</span>{' '}
                {openMessage.toAddresses.map((a) => a.name || a.email).join(', ') ||
                  '(no recipient)'}
              </div>
              {openMessage.receivedAt && (
                <div>{new Date(openMessage.receivedAt).toLocaleString()}</div>
              )}
            </div>
            {/* Labels — clickable chips (drill down to a label) + the apply/remove
                picker. Chips here are outside the row button, so they can filter. */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <LabelChips labels={openMessage.labels} onSelect={openLabel} />
              <LabelPickerPopover
                accountId={openMessage.accountId}
                messageId={openMessage.messageId}
                applied={openMessage.labels ?? []}
                onManage={() => setLabelManagerOpen(true)}
              />
              {/* Unsubscribe — one-click posts server-side; url/mailto open here.
                  Hidden when the sender advertised no List-Unsubscribe header. */}
              {(() => {
                const { method, targets } = unsubscribeMethod(
                  openMessage.listUnsubscribe,
                  openMessage.listUnsubscribePost,
                );
                if (method === 'none') return null;
                return (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 gap-1"
                    disabled={unsubscribe.isPending}
                    title="Unsubscribe from this sender"
                    onClick={() => {
                      if (method === 'one-click') {
                        unsubscribe.mutate(openMessage.messageId);
                      } else if (method === 'url' && targets.httpsUrl) {
                        window.open(targets.httpsUrl, '_blank', 'noopener,noreferrer');
                      } else if (method === 'mailto' && targets.mailto) {
                        window.open(targets.mailto);
                      }
                    }}
                  >
                    <MailX className="h-3.5 w-3.5" /> Unsubscribe
                  </Button>
                );
              })()}
            </div>
            {openMessage.bodyHtml ? (
              <EmailHtmlBody
                html={openMessage.bodyHtml}
                messageId={openMessage.messageId}
                attachments={openAttachments ?? []}
              />
            ) : (
              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                {messageBodyText(openMessage) || '(no content)'}
              </div>
            )}
            {openAttachments && openAttachments.length > 0 && (
              <div className="mt-6 border-t pt-4">
                <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                  <Paperclip className="h-3.5 w-3.5" /> Attachments ({openAttachments.length})
                </div>
                <ul className="space-y-1">
                  {openAttachments.map((a) => (
                    <li
                      key={a.attachmentId}
                      className="flex items-center justify-between gap-2 rounded border bg-card px-2 py-1.5 text-sm"
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{a.filename}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {Math.max(1, Math.round(a.sizeBytes / 1024))} KB
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center">
                        {isPreviewableAttachment(a.mimeType, a.filename) && activeMessageId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            title="Preview"
                            aria-label={`Preview ${a.filename}`}
                            onClick={() =>
                              setPreviewAtt({
                                attachmentId: a.attachmentId,
                                filename: a.filename,
                                mimeType: a.mimeType,
                                documentId: a.documentId,
                              })
                            }
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {a.documentId && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2"
                            title="Save a copy to a Vault folder"
                            aria-label={`Save a copy of ${a.filename} to a Vault folder`}
                            onClick={() =>
                              setSaveToVaultDoc({
                                attachmentId: a.attachmentId,
                                fileName: a.filename,
                              })
                            }
                          >
                            <FolderInput className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {/* Calendar invite (.ics) → add to the Vault calendar. */}
                        {(a.mimeType.toLowerCase() === 'text/calendar' ||
                          a.filename.toLowerCase().endsWith('.ics')) &&
                          activeMessageId && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              title="Add to calendar"
                              aria-label={`Add ${a.filename} to calendar`}
                              disabled={addCalendarInvite.isPending}
                              onClick={() =>
                                addCalendarInvite.mutate({
                                  messageId: activeMessageId,
                                  attachmentId: a.attachmentId,
                                })
                              }
                            >
                              <CalendarPlus className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          title="Download"
                          aria-label={`Download ${a.filename}`}
                          onClick={() => downloadAttachment(a.attachmentId, a.filename)}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>
        )}
      </div>

      <MailboxComposeDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        accounts={accounts}
        // Single-account mode uses the active account; in all-accounts mode
        // (accountId undefined) default the From to the TOP account in the list
        // rather than the primary, per the compose-from request.
        defaultAccountId={accountId ?? accounts?.[0]?.accountId}
        initialDraft={composeDraft}
        onDiscardDraft={
          composeDraftLite
            ? () => {
                discardDraft.mutate(composeDraftLite.messageId);
                setComposeOpen(false);
              }
            : undefined
        }
        onRestoreDraft={openCompose}
      />
      <ScheduledSendsDialog open={scheduledOpen} onOpenChange={setScheduledOpen} />
      <MailboxSnoozeDialog
        open={snoozeOpen}
        onOpenChange={setSnoozeOpen}
        messageId={activeMessageId}
        onSnoozed={() => setActiveMessageId(null)}
      />
      <MailboxSnoozedDialog
        open={snoozedListOpen}
        onOpenChange={setSnoozedListOpen}
        accountId={toolsAccountId}
      />
      <ComposerSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        accountId={toolsAccountId}
      />
      <AddressBookDialog open={contactsOpen} onOpenChange={setContactsOpen} />
      <FiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        defaultAccountId={toolsAccountId}
      />
      <CategoryManagerDialog open={categoryManagerOpen} onOpenChange={setCategoryManagerOpen} />
      <LabelManagerDialog
        open={labelManagerOpen}
        onOpenChange={setLabelManagerOpen}
        accountId={openMessage?.accountId ?? contentAccountId}
      />
      <BulkCategorizeDialog
        open={bulkCategorizeOpen}
        onOpenChange={setBulkCategorizeOpen}
        // Same scope the By-Category overview grouped: the unified inbox, or the
        // focused account+folder.
        scope={
          isMerged
            ? { folderSystemRole: 'inbox' }
            : { accountId: contentAccountId, folderId: contentFolderId }
        }
      />
      <ScreenerDialog
        open={screenerOpen}
        onOpenChange={setScreenerOpen}
        defaultAccountId={toolsAccountId}
      />
      <SpamSettingsDialog
        open={spamOpen}
        onOpenChange={setSpamOpen}
        defaultAccountId={toolsAccountId}
      />
      <VacationResponderDialog
        open={vacationOpen}
        onOpenChange={setVacationOpen}
        defaultAccountId={toolsAccountId}
      />
      {openMessage && (
        <AddToContactDialog
          open={addContactOpen}
          onOpenChange={setAddContactOpen}
          initialEmail={openMessage.fromAddress}
          initialName={openMessage.fromName}
        />
      )}
      <AttachmentPreviewDialog
        open={!!previewAtt}
        onOpenChange={(o) => !o && setPreviewAtt(null)}
        messageId={activeMessageId}
        attachment={previewAtt}
        onSaveToVault={() => {
          if (previewAtt) {
            setSaveToVaultDoc({
              attachmentId: previewAtt.attachmentId,
              fileName: previewAtt.filename,
            });
            setPreviewAtt(null);
          }
        }}
      />
      <SaveToVaultFolderDialog
        open={!!saveToVaultDoc}
        onOpenChange={(o) => !o && setSaveToVaultDoc(null)}
        messageId={activeMessageId}
        attachment={saveToVaultDoc}
      />
      <AlertDialog open={confirmBulkDelete} onOpenChange={(o) => !o && setConfirmBulkDelete(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {selectedCount} {selectedCount === 1 ? 'message' : 'messages'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {viewingTrash
                ? 'These are in Trash and will be permanently deleted. This cannot be undone.'
                : 'They’ll be moved to Trash. You can undo this, or restore them from Trash until you empty it.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                runBulk((msgs) => actTrash(msgs), { closesOpen: true });
                setConfirmBulkDelete(false);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Whole-folder ("select all in folder") confirm — these act on EVERY
          matching message server-side, not just the loaded rows, so they're
          gated behind a deliberate confirm rather than an inline undo. */}
      <AlertDialog
        open={!!confirmWholeFolder}
        onOpenChange={(o) => !o && setConfirmWholeFolder(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmWholeFolder?.kind === 'archive'
                ? `Archive all${wholeFolderCountLabel} messages?`
                : confirmWholeFolder?.kind === 'trash'
                  ? `Delete all${wholeFolderCountLabel} messages?`
                  : `Move all${wholeFolderCountLabel} messages to ${
                      confirmWholeFolder?.kind === 'move' ? confirmWholeFolder.folderName : ''
                    }?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This applies to every message in {isUnified ? 'your inboxes' : 'this folder'} that
              matches the current view — not just the ones loaded on screen.
              {confirmWholeFolder?.kind === 'trash'
                ? ' They’ll be moved to Trash; restore them from there until you empty it.'
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeWholeFolder}>
              {confirmWholeFolder?.kind === 'archive'
                ? 'Archive all'
                : confirmWholeFolder?.kind === 'trash'
                  ? 'Delete all'
                  : 'Move all'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Folder management: shared create/rename name dialog + empty/delete confirms. */}
      <FolderNameDialog
        open={!!folderNameDialog}
        mode={folderNameDialog?.mode ?? 'create'}
        initialName={folderNameDialog?.mode === 'rename' ? folderNameDialog.current : ''}
        parentName={folderNameDialog?.mode === 'create' ? folderNameDialog.parentName : undefined}
        pending={createFolder.isPending || renameFolder.isPending}
        onSubmit={submitFolderName}
        onOpenChange={(o) => !o && setFolderNameDialog(null)}
      />
      <AlertDialog
        open={!!confirmEmptyFolder}
        onOpenChange={(o) => !o && setConfirmEmptyFolder(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Empty “{confirmEmptyFolder?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmEmptyFolder?.role === 'trash'
                ? 'Every message in Trash will be permanently deleted. This cannot be undone.'
                : 'Every message in this folder will be moved to Trash. You can restore them from there until you empty it.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={emptyFolderConfirmed}>
              {confirmEmptyFolder?.role === 'trash' ? 'Empty Trash' : 'Empty folder'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={!!confirmDeleteFolder}
        onOpenChange={(o) => !o && setConfirmDeleteFolder(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{confirmDeleteFolder?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              The folder is removed from the mail server. Any messages still in it are deleted with
              it — move anything you want to keep first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteFolderConfirmed}>Delete folder</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
